import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Pencil, Trash2, Building2, Search, Copy, Eye, EyeOff, Users, GraduationCap, ClipboardList, ExternalLink, Clock, BookOpen, ChevronDown, ChevronUp, AlertTriangle, MapPin, Globe, MessageSquare, LayoutGrid, List, ChevronLeft, ChevronRight, UserCheck, UserX, KeyRound, HelpCircle, CheckCircle2, RefreshCw, HeartHandshake, Loader2, UserPlus, ShieldAlert, Power, PowerOff } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { UserManagerTab } from '../../components/gestao/UserManagerTab';
import { gerarSenha } from '../../components/invite';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal, PasswordConfirmModal, RowActionMenu, type RowActionItem, DataToolbar } from '../../components/ui';
import { formatPhone, formatCPF } from '../../utils/cpfUtils';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';
import { InviteResultDialog, normalizeActivationLink } from '../../components/invite';
import { DemoInstallWizard } from '../../components/gestao/DemoInstallWizard';
import { EscolaAcessosRapidosModal } from '../../components/gestao/EscolaAcessosRapidosModal';
import { useDevStore } from '../../store/useDevStore';
import { useAuthStore } from '../../store/authStore';
import { hasPermission } from '../../auth/permissions';
import { AlunoFichaDesktop } from '../admin/AlunoFichaDesktop';
import { UsuarioEditModal } from '../../components/gestao/UsuarioEditModal';
import { ResponsavelFichaDesktop, ResponsavelFichaFooter } from '../admin/ResponsavelFichaDesktop';
import { CadastroEscolarInfoModal } from '../../components/gestao/CadastroEscolarInfoModal';

// Custom Leaflet pin for Escola
const escolaMarkerPinIcon = L.divIcon({
  className: '',
  html: `<div style="font-size: 30px; transform: translate(-15px, -30px); filter: drop-shadow(0 4px 6px rgba(0,0,0,0.4));">🏫</div>`,
  iconSize: [30, 30],
  iconAnchor: [0, 0]
});

// Component to dynamically re-center map
function MapCenterController({ center }: { center: [number, number] }) {
  const map = useMap();
  const lat = center[0];
  const lng = center[1];
  useEffect(() => {
    if (isFinite(lat) && isFinite(lng)) {
      map.setView([lat, lng], 14);
    }
  }, [lat, lng, map]);
  return null;
}

// Component to handle map clicks
function MapClickEvents({ onClick }: { onClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onClick(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
}

interface Rede { id: string; nome: string; }
interface Estado { id: string; nome: string; sigla?: string; redeId?: string; }
interface Municipio { id: string; nome: string; estadoId?: string; ativo?: boolean; }
interface CatalogoEscola { id: number; nome: string; codigoInep: string; uf: string; municipio: string; endereco?: string; telefone?: string; dependenciaAdministrativa?: string; latitude?: number; longitude?: number; }
interface Escola {
  id: string; nome: string; codigo: string; tipoRede: string; endereco?: string; cidade?: string; uf?: string;
  telefone?: string; email?: string; nomeDiretor?: string; codigoINEP?: string; municipioId?: string; ativo: boolean;
  latitude?: number | null; longitude?: number | null;
  wizardConcluido?: boolean; wizardEtapaAtual?: number; wizardConcluidoEm?: string | null;
  municipio?: { id: string; nome: string; estado?: { id: string; nome: string; sigla: string } };
  _count?: { turmas: number; alunos: number; professorEscolas?: number };
  criadoEm: string;
  isDemo?: boolean;
  demoAtiva?: boolean;
  demoCriadaEm?: string | null;
  demoExpiraEm?: string | null;
  enviarPushReais?: boolean;
}

const TIPO_REDE_OPTIONS = [
  { value: 'MUNICIPAL', label: 'Municipal' },
  { value: 'ESTADUAL', label: 'Estadual' },
  { value: 'PARTICULAR', label: 'Particular' },
];

// ─── Helpers de Validação para Responsáveis ───
function isValidCPF(cpf: string): boolean {
  const clean = cpf.replace(/\D/g, '');
  if (clean.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(clean)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(clean.charAt(i), 10) * (10 - i);
  let rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(clean.charAt(9), 10)) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(clean.charAt(i), 10) * (11 - i);
  rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(clean.charAt(10), 10)) return false;

  return true;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function EscolaResponsaveisTab({ escolaId }: { escolaId: string }) {
  const [responsaveis, setResponsaveis] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [editingResp, setEditingResp] = useState<any | null>(null);
  const [showNewResp, setShowNewResp] = useState(false);
  const [selectedRespId, setSelectedRespId] = useState<string | null>(null);
  const [modalTitleResp, setModalTitleResp] = useState<string>('Convidar Novo Responsável');
  const [newRespForm, setNewRespForm] = useState({ nome: '', email: '', telefone: '', cpf: '', dataNascimento: '' });
  const [creatingResp, setCreatingResp] = useState(false);
  const [inviteResult, setInviteResult] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ nome: '', email: '', telefone: '', cpf: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [blockConfirm, setBlockConfirm] = useState<any | null>(null);
  const [fichaResp, setFichaResp] = useState<any | null>(null);
  const [fichaAlunoId, setFichaAlunoId] = useState<string | null>(null);
  const [selectedUserForModal, setSelectedUserForModal] = useState<any | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [showInfoCadastroEscolar, setShowInfoCadastroEscolar] = useState(false);

  const handleResendInvite = async (userId: string) => {
    setResendingId(userId);
    try {
      const { data } = await api.post(`/admin/usuarios/${userId}/resend`);
      toast.success('Novo link de ativação gerado com sucesso!');
      fetchResponsaveis();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      }
      const activationLink = data?.activationLink || (data?.user?.tokenAtivacao ? normalizeActivationLink(data.user.tokenAtivacao) : '');
      if (activationLink) {
        setInviteResult({
          nome: data.user?.nome,
          email: data.user?.email,
          telefone: data.user?.telefone,
          perfil: 'RESPONSÁVEL',
          activationLink,
          whatsappLink: data.whatsappLink || null,
          emailSent: data.emailSent !== false,
        });
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao reenviar convite');
    } finally {
      setResendingId(null);
    }
  };

  const openEditResp = (r: any) => {
    const targetUserId = r.usuarioId || r.usuario?.id;
    if (targetUserId) {
      setSelectedUserForModal({
        id: targetUserId,
        nome: r.nome || '',
        email: r.email || '',
        telefone: r.telefone ? formatPhone(r.telefone) : '',
        cpf: r.cpf ? formatCPF(r.cpf) : '',
        dataNascimento: r.dataNascimento ? String(r.dataNascimento).slice(0, 10) : '',
        perfil: 'RESPONSAVEL',
        escolaId,
        statusConta: r.usuario?.statusConta || r.statusConta || 'ATIVA',
        ativo: r.usuario?.ativo !== false && r.ativo !== false,
      });
    } else {
      setEditingResp(r);
      setEditForm({
        nome: r.nome || '',
        email: r.email || '',
        telefone: r.telefone ? formatPhone(r.telefone) : '',
        cpf: r.cpf ? formatCPF(r.cpf) : '',
      });
    }
  };

  const fetchResponsaveis = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(ROUTES.responsaveis.listar(escolaId));
      setResponsaveis(Array.isArray(data) ? data : []);
    } catch {
      setResponsaveis([]);
    } finally {
      setLoading(false);
    }
  }, [escolaId]);

  useEffect(() => {
    fetchResponsaveis();
    const handleRefetch = () => { fetchResponsaveis(); };
    window.addEventListener('ac-data-refetch', handleRefetch);
    return () => window.removeEventListener('ac-data-refetch', handleRefetch);
  }, [fetchResponsaveis]);

  const filtrados = useMemo(() => {
    const q = search.toLowerCase().trim();
    const list = responsaveis.filter((r: any) => {
      return !q || [r.nome, r.email || '', r.telefone || '', r.cpf || ''].join(' ').toLowerCase().includes(q);
    });

    // Ordena: última edição/alteração/criação sempre no topo
    return list.sort((a, b) => {
      const dateA = new Date(a.usuario?.atualizadoEm || a.atualizadoEm || a.usuario?.criadoEm || a.criadoEm || 0).getTime();
      const dateB = new Date(b.usuario?.atualizadoEm || b.atualizadoEm || b.usuario?.criadoEm || b.criadoEm || 0).getTime();
      if (dateB !== dateA) return dateB - dateA;
      return (a.nome || '').localeCompare(b.nome || '');
    });
  }, [responsaveis, search]);

  // Botão superior principal: abre com campos limpos e título Convidar Novo Responsável
  const handleOpenNewResp = () => {
    setSelectedRespId(null);
    setModalTitleResp('Convidar Novo Responsável');
    setNewRespForm({ nome: '', email: '', telefone: '', cpf: '', dataNascimento: '' });
    setShowNewResp(true);
  };

  // Botão na linha de Cadastro Escolar: pré-preenche dados e define título Cadastrar Usuário para Responsável
  const handleActivateResp = (r: any) => {
    setSelectedRespId(r.id);
    setModalTitleResp('Cadastrar Usuário para Responsável');
    setNewRespForm({
      nome: r.nome || '',
      email: r.email || '',
      telefone: r.telefone ? formatPhone(r.telefone) : '',
      cpf: r.cpf ? formatCPF(r.cpf) : '',
      dataNascimento: r.dataNascimento ? r.dataNascimento.split('T')[0] : '',
    });
    setShowNewResp(true);
  };

  const handleBlockResp = async () => {
    if (!blockConfirm) return;
    try {
      if (blockConfirm.usuarioId) {
        await api.patch(`/admin/usuarios/${blockConfirm.usuarioId}/toggle`);
        toast.success('Acesso do usuário desativado!');
      } else {
        toast.info('Este responsável não possui conta de usuário ativa.');
      }
      setBlockConfirm(null);
      fetchResponsaveis();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao desativar usuário');
    }
  };

  const handleReactivateResp = async (r: any) => {
    const targetUserId = r.usuarioId || r.usuario?.id;
    if (!targetUserId) return;
    try {
      const { data } = await api.patch(`/admin/usuarios/${targetUserId}/toggle`);
      if (data?.statusConta === 'PENDENTE' || data?.reconvidado || !data?.ativadoEm) {
        toast.success('Responsável reativado! Novo link de ativação de 72h gerado.');
        const actLink = data.activationLink || (data.tokenAtivacao ? normalizeActivationLink(data.tokenAtivacao) : '');
        if (actLink) {
          setInviteResult({
            nome: r.nome || data.nome,
            email: r.email || data.email,
            telefone: r.telefone || data.telefone,
            perfil: 'RESPONSÁVEL',
            activationLink: actLink,
            whatsappLink: (r.telefone || data.telefone) ? `https://wa.me/${(r.telefone || data.telefone).replace(/\D/g, '')}?text=${encodeURIComponent(`Olá, ${r.nome}! Sua conta foi reativada. Acesse para definir sua senha: ${actLink}`)}` : null,
            emailSent: true,
          });
        }
      } else {
        toast.success('Responsável reativado com sucesso!');
      }
      fetchResponsaveis();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao reativar responsável');
    }
  };

  const handleSaveRespEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingResp) return;
    setSavingEdit(true);
    try {
      await api.put(`/escolas/${escolaId}/responsaveis/${editingResp.id}`, {
        nome: editForm.nome.trim(),
        email: editForm.email.trim() || undefined,
        telefone: editForm.telefone.replace(/\D/g, '') || undefined,
        cpf: editForm.cpf.replace(/\D/g, '') || undefined,
      });
      toast.success('Responsável atualizado com sucesso!');
      setEditingResp(null);
      fetchResponsaveis();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao salvar alterações');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCreateNewResp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRespForm.nome.trim()) {
      toast.warning('Dados Incompletos: Informe o nome completo do responsável.');
      return;
    }
    if (!newRespForm.email.trim() || !isValidEmail(newRespForm.email)) {
      toast.warning('Dados Incorretos: Informe um endereço de e-mail válido.');
      return;
    }
    const cleanCpf = newRespForm.cpf.replace(/\D/g, '');
    if (!cleanCpf || cleanCpf.length !== 11) {
      toast.warning('Dados Incompletos: O CPF é obrigatório e deve ter 11 dígitos.');
      return;
    }
    if (!isValidCPF(cleanCpf)) {
      toast.warning('Dados Incorretos: O CPF informado possui dígitos verificadores inválidos.');
      return;
    }
    if (!newRespForm.dataNascimento) {
      toast.warning('Dados Incompletos: A data de nascimento é obrigatória para o cadastro.');
      return;
    }

    setCreatingResp(true);
    try {
      // 1. Cria ou convida como usuário com perfil RESPONSAVEL
      const { data: userRes } = await api.post('/admin/usuarios', {
        nome: newRespForm.nome.trim(),
        email: newRespForm.email.trim(),
        telefone: newRespForm.telefone ? newRespForm.telefone.replace(/\D/g, '') : undefined,
        cpf: cleanCpf,
        dataNascimento: new Date(newRespForm.dataNascimento).toISOString(),
        perfil: 'RESPONSAVEL',
        escolaId,
        responsavelId: selectedRespId || undefined,
      });

      const activationLink = userRes?.activationLink || (userRes?.tokenAtivacao ? normalizeActivationLink(userRes.tokenAtivacao) : '');
      const whatsappLink = userRes?.whatsappLink || null;

      toast.success('Convite gerado com sucesso!');
      setShowNewResp(false);
      setSelectedRespId(null);
      setNewRespForm({ nome: '', email: '', telefone: '', cpf: '', dataNascimento: '' });
      fetchResponsaveis();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      }

      if (activationLink) {
        setInviteResult({
          nome: newRespForm.nome.trim(),
          email: newRespForm.email.trim(),
          telefone: newRespForm.telefone,
          perfil: 'RESPONSÁVEL',
          activationLink,
          whatsappLink,
          emailSent: userRes?.emailEnviado !== false,
        });
      }
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Erro ao gerar convite';
      const isConflict = errorMsg.toLowerCase().includes('cpf') || 
                         errorMsg.toLowerCase().includes('e-mail') || 
                         errorMsg.toLowerCase().includes('email') || 
                         errorMsg.toLowerCase().includes('já existe') || 
                         errorMsg.toLowerCase().includes('já está cadastrado') ||
                         errorMsg.toLowerCase().includes('em uso') ||
                         err?.response?.status === 409 ||
                         err?.response?.status === 400;

      if (isConflict) {
        toast.warning(`Conflito de Dados: ${errorMsg}`);
      } else {
        toast.error(errorMsg, err);
      }
    } finally {
      setCreatingResp(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '.85rem', color: '#6b7280' }}>
          {filtrados.length} {filtrados.length === 1 ? 'responsável' : 'responsáveis'}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ position: 'relative', width: 220 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar responsável..."
              style={{ width: '100%', height: 32, padding: '0 10px 0 32px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '.8rem', background: '#ffffff', color: '#1e293b' }}
            />
          </div>
          <button
            className="btn-primary"
            onClick={handleOpenNewResp}
            style={{ padding: '6px 14px', fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
          >
            <UserPlus size={14} /> Convidar Responsável
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px 10px', color: '#9ca3af' }}>
          <Loader2 className="animate-spin" size={24} style={{ margin: '0 auto 8px', display: 'block' }} />
          <span style={{ fontSize: '.85rem' }}>Carregando responsáveis...</span>
        </div>
      ) : filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px 10px', color: '#9ca3af' }}>
          <Users size={32} style={{ marginBottom: 8, opacity: 0.4, margin: '0 auto', display: 'block' }} />
          <p style={{ margin: 0, fontSize: '.85rem' }}>Nenhum responsável cadastrado nesta escola</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 380, overflowY: 'auto' }}>
          {filtrados.map((r: any) => {
            const temConta = Boolean(r.usuarioId || r.usuario?.id);
            const targetUserId = r.usuarioId || r.usuario?.id;
            const statusConta = (r.usuario?.statusConta || r.statusConta || '').toUpperCase();
            const isDesativado = temConta && (r.usuario?.ativo === false || r.ativo === false || statusConta === 'SUSPENSA' || statusConta === 'BLOQUEADA');
            const isAtivo = temConta && !isDesativado && (statusConta === 'ATIVA' || (r.usuario?.ativo && statusConta !== 'PENDENTE'));
            const isExpirado = temConta && !isDesativado && !isAtivo && Boolean(r.usuario?.tokenExpiraEm && new Date(r.usuario.tokenExpiraEm).getTime() <= Date.now());
            const isAguardando = temConta && !isDesativado && !isAtivo && !isExpirado;
            const contato = [r.email, r.telefone ? formatPhone(r.telefone) : null, r.cpf ? `CPF: ${formatCPF(r.cpf)}` : null].filter(Boolean).join(' • ');

            let badgeBg = 'rgba(100, 116, 139, 0.12)';
            let badgeColor = '#475569';
            let badgeBorder = 'rgba(100, 116, 139, 0.25)';
            let badgeText = 'CADASTRO ESCOLAR';

            if (temConta) {
              if (isDesativado) {
                badgeBg = 'rgba(239, 68, 68, 0.12)';
                badgeColor = '#ef4444';
                badgeBorder = 'rgba(239, 68, 68, 0.25)';
                badgeText = 'DESATIVADO';
              } else if (isAtivo) {
                badgeBg = 'rgba(16, 185, 129, 0.12)';
                badgeColor = '#10b981';
                badgeBorder = 'rgba(16, 185, 129, 0.25)';
                badgeText = 'ATIVO';
              } else if (isExpirado) {
                badgeBg = 'rgba(239, 68, 68, 0.12)';
                badgeColor = '#ef4444';
                badgeBorder = 'rgba(239, 68, 68, 0.25)';
                badgeText = 'LINK EXPIRADO';
              } else if (isAguardando) {
                badgeBg = 'rgba(245, 158, 11, 0.12)';
                badgeColor = '#d97706';
                badgeBorder = 'rgba(245, 158, 11, 0.25)';
                badgeText = 'AGUARDANDO';
              }
            }

            return (
              <div key={r.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                borderRadius: 8, border: '1px solid var(--border, #e5e7eb)', background: 'var(--bg-muted, #f9fafb)',
                transition: 'background .15s',
              }}>
                {/* Avatar */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 700, fontSize: '.8rem',
                }}>
                  {r.nome?.charAt(0)?.toUpperCase() || '?'}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.nome}
                  </div>
                  <div style={{ fontSize: '.75rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {contato || 'Sem contatos adicionais'}
                  </div>
                </div>

                {/* Status badge */}
                <span
                  onClick={(e) => {
                    if (!temConta) {
                      e.stopPropagation();
                      setShowInfoCadastroEscolar(true);
                    }
                  }}
                  style={{
                    padding: '3px 8px', borderRadius: 6, fontSize: '.64rem', fontWeight: 700,
                    background: badgeBg,
                    color: badgeColor,
                    border: `1px solid ${badgeBorder}`,
                    textTransform: 'uppercase', letterSpacing: '.03em',
                    flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4,
                    cursor: !temConta ? 'pointer' : 'default',
                  }}
                  title={!temConta ? 'Clique para entender o status de Cadastro Escolar' : undefined}
                >
                  {badgeText}
                </span>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {/* Botão Visualizar Ficha */}
                  <button
                    className="action-btn"
                    onClick={() => setFichaResp(r)}
                    title="Visualizar Ficha do Responsável"
                  >
                    <Eye size={14} />
                  </button>

                  {temConta && (
                  <button
                    className="action-btn"
                    onClick={() => openEditResp(r)}
                    title="Editar Conta de Usuário do Responsável"
                  >
                    <Pencil size={14} />
                  </button>
                  )}

                  {/* Reenviar Convite se pendente ou expirado e NÃO desativado */}
                  {temConta && targetUserId && !isDesativado && (isExpirado || isAguardando) && (
                    <button
                      className="action-btn"
                      style={{ color: '#d97706' }}
                      onClick={() => handleResendInvite(targetUserId)}
                      disabled={resendingId === targetUserId}
                      title="Reenviar / Regerar Novo Link de Ativação de 72h"
                    >
                      {resendingId === targetUserId ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                    </button>
                  )}

                  {temConta ? (
                    isDesativado ? (
                      <button
                        className="action-btn"
                        style={{ color: '#10b981' }}
                        onClick={() => handleReactivateResp(r)}
                        title="Reativar Acesso do Responsável"
                      >
                        <UserCheck size={14} />
                      </button>
                    ) : (
                      <button
                        className="action-btn action-btn-danger"
                        onClick={() => setBlockConfirm(r)}
                        title="Desativar / Bloquear Acesso"
                      >
                        <UserX size={14} />
                      </button>
                    )
                  ) : (
                    <button
                      className="action-btn"
                      onClick={() => handleActivateResp(r)}
                      disabled={activatingId === r.id}
                      title="Cadastrar Usuário para Responsável"
                    >
                      {activatingId === r.id ? <Loader2 className="animate-spin" size={14} /> : <UserPlus size={14} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de Edição de Responsável (Legado/Fallback) */}
      {editingResp && (
        <Modal
          open={!!editingResp}
          onClose={() => setEditingResp(null)}
          title={`Editar Responsável: ${editingResp.nome}`}
          size="default"
        >
          <form onSubmit={handleSaveRespEdit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Nome Completo *</label>
              <input
                className="form-input"
                required
                value={editForm.nome}
                onChange={e => setEditForm(f => ({ ...f, nome: e.target.value }))}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">E-mail (para acesso ao App)</label>
                <input
                  className="form-input"
                  type="email"
                  value={editForm.email}
                  onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@exemplo.com"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Telefone</label>
                <input
                  className="form-input"
                  value={editForm.telefone}
                  onChange={e => setEditForm(f => ({ ...f, telefone: formatPhone(e.target.value) }))}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">CPF</label>
              <input
                className="form-input"
                value={editForm.cpf}
                onChange={e => setEditForm(f => ({ ...f, cpf: formatCPF(e.target.value) }))}
                placeholder="000.000.000-00"
                maxLength={14}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button type="button" className="btn-cancel" onClick={() => setEditingResp(null)}>Cancelar</button>
              <button type="submit" className="btn-save" disabled={savingEdit}>
                {savingEdit ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Confirm Bloqueio */}
      <ConfirmModal
        open={!!blockConfirm}
        title="Desativar Acesso do Responsável"
        message={blockConfirm ? `Deseja desativar o acesso de "${blockConfirm.nome}" ao aplicativo?` : ''}
        confirmLabel="Desativar Acesso"
        onCancel={() => setBlockConfirm(null)}
        onConfirm={() => void handleBlockResp()}
      />

      {/* Modal Convidar / Cadastrar Usuário para Responsável */}
      {showNewResp && (
        <Modal
          open={showNewResp}
          onClose={() => setShowNewResp(false)}
          title={modalTitleResp}
          size="default"
        >
          <form onSubmit={handleCreateNewResp} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Nome Completo *</label>
              <input
                className="form-input"
                required
                value={newRespForm.nome}
                onChange={e => setNewRespForm(f => ({ ...f, nome: e.target.value }))}
                placeholder="Ex: Carlos Eduardo Silva"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">E-mail (para ativação do App) *</label>
                <input
                  className="form-input"
                  type="email"
                  required
                  value={newRespForm.email}
                  onChange={e => setNewRespForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="carlos@exemplo.com"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Telefone / WhatsApp *</label>
                <input
                  className="form-input"
                  required
                  value={newRespForm.telefone}
                  onChange={e => setNewRespForm(f => ({ ...f, telefone: formatPhone(e.target.value) }))}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">CPF *</label>
                <input
                  className="form-input"
                  required
                  value={newRespForm.cpf}
                  onChange={e => setNewRespForm(f => ({ ...f, cpf: formatCPF(e.target.value) }))}
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Data de Nascimento *</label>
                <input
                  className="form-input"
                  type="date"
                  required
                  value={newRespForm.dataNascimento}
                  onChange={e => setNewRespForm(f => ({ ...f, dataNascimento: e.target.value }))}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button type="button" className="btn-cancel" onClick={() => setShowNewResp(false)}>Cancelar</button>
              <button type="submit" className="btn-save" disabled={creatingResp}>
                {creatingResp ? 'Convidando...' : 'Enviar Convite'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Dialog Unificado de Resultado de Convite */}
      {inviteResult && (
        <InviteResultDialog
          open={!!inviteResult}
          nome={inviteResult.nome}
          email={inviteResult.email}
          perfil={inviteResult.perfil}
          telefone={inviteResult.telefone}
          activationLink={inviteResult.activationLink}
          whatsappLink={inviteResult.whatsappLink}
          emailSent={inviteResult.emailSent}
          onClose={() => setInviteResult(null)}
        />
      )}

      {/* Modal Completo de Edição de Usuário (Dados, Permissões, Senha, Link) */}
      {selectedUserForModal && (
        <UsuarioEditModal
          open={!!selectedUserForModal}
          userId={selectedUserForModal.id}
          initialData={selectedUserForModal}
          onClose={() => setSelectedUserForModal(null)}
          onUpdated={fetchResponsaveis}
        />
      )}

      {/* Modal Informativo: O que significa Cadastro Escolar */}
      <CadastroEscolarInfoModal
        open={showInfoCadastroEscolar}
        onClose={() => setShowInfoCadastroEscolar(false)}
        tipo="responsavel"
      />


      {/* Ficha do Responsável — Portal no body (evita compressão pelo modal pai) */}
      {fichaResp && typeof document !== 'undefined' && createPortal(
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setFichaResp(null);
          }}
        >
          <div
            style={{
              width: '96vw',
              maxWidth: 1100,
              maxHeight: '92vh',
              background: '#ffffff',
              borderRadius: 14,
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <ResponsavelFichaDesktop
              responsavelId={fichaResp.id}
              escolaId={escolaId}
              canManage={false}
              onClose={() => setFichaResp(null)}
              onEdit={() => {}}
              onDeactivate={() => {}}
              onReactivate={() => {}}
              canDeactivate={{ ok: false }}
              onInvited={() => { fetchResponsaveis(); }}
              onOpenAluno={(alunoId) => setFichaAlunoId(alunoId)}
            />
          </div>
        </div>,
        document.body
      )}

      {/* Ficha do Aluno — aberta a partir da ficha do responsável, portal no body */}
      {fichaAlunoId && typeof document !== 'undefined' && createPortal(
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'rgba(15, 23, 42, 0.80)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setFichaAlunoId(null); }}
        >
          <div
            style={{
              width: '96vw',
              maxWidth: 1280,
              height: '92vh',
              maxHeight: 900,
              background: '#ffffff',
              borderRadius: 14,
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <AlunoFichaDesktop
              alunoId={fichaAlunoId}
              escolaId={escolaId}
              canManage={false}
              onClose={() => setFichaAlunoId(null)}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function EscolaAlunosTab({ escolaId }: { escolaId: string }) {
  const [alunos, setAlunos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedAlunoId, setSelectedAlunoId] = useState<string | null>(null);
  const [showInfoCadastroEscolar, setShowInfoCadastroEscolar] = useState(false);

  const fetchAlunos = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(ROUTES.alunos.listar(escolaId));
      setAlunos(Array.isArray(data) ? data : []);
    } catch {
      setAlunos([]);
    } finally {
      setLoading(false);
    }
  }, [escolaId]);

  useEffect(() => {
    fetchAlunos();
  }, [fetchAlunos]);

  const filtrados = useMemo(() => {
    const q = search.toLowerCase().trim();
    return alunos.filter((a: any) => {
      return !q || [a.nome, a.matricula || '', a.turma?.nome || '', a.cpf || ''].join(' ').toLowerCase().includes(q);
    });
  }, [alunos, search]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 10 }}>
        <span style={{ fontSize: '.85rem', color: '#6b7280' }}>
          {filtrados.length} {filtrados.length === 1 ? 'aluno matriculado' : 'alunos matriculados'}
        </span>
        <div style={{ position: 'relative', width: 260 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome, matrícula, turma..."
            style={{ width: '100%', height: 32, padding: '0 10px 0 32px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '.8rem', background: '#ffffff', color: '#1e293b' }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px 10px', color: '#9ca3af' }}>
          <Loader2 className="animate-spin" size={24} style={{ margin: '0 auto 8px', display: 'block' }} />
          <span style={{ fontSize: '.85rem' }}>Carregando alunos...</span>
        </div>
      ) : filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px 10px', color: '#9ca3af' }}>
          <GraduationCap size={32} style={{ marginBottom: 8, opacity: 0.4, margin: '0 auto', display: 'block' }} />
          <p style={{ margin: 0, fontSize: '.85rem' }}>Nenhum aluno matriculado nesta escola</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 380, overflowY: 'auto' }}>
          {filtrados.map((a: any) => {
            const sub = [a.turma?.nome ? `Turma: ${a.turma.nome}` : null, `Matrícula: ${a.matricula || '—'}`, a.cpf ? `CPF: ${formatCPF(a.cpf)}` : null].filter(Boolean).join(' • ');
            return (
              <div
                key={a.id}
                onClick={() => setSelectedAlunoId(a.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  borderRadius: 8, border: '1px solid var(--border, #e5e7eb)', background: 'var(--bg-muted, #f9fafb)',
                  cursor: 'pointer', transition: 'all .15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-muted, #f9fafb)'}
              >
                {/* Avatar */}
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: 700, fontSize: '.8rem',
                }}>
                  {a.nome?.charAt(0)?.toUpperCase() || '?'}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.nome}
                  </div>
                  <div style={{ fontSize: '.75rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {sub}
                  </div>
                </div>

                {/* Badge: aluno é cadastro escolar, não usuário da plataforma */}
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowInfoCadastroEscolar(true);
                  }}
                  style={{
                    padding: '3px 8px',
                    borderRadius: 6,
                    fontSize: '.64rem',
                    fontWeight: 700,
                    background: 'rgba(100, 116, 139, 0.12)',
                    color: '#475569',
                    border: '1px solid rgba(100, 116, 139, 0.25)',
                    textTransform: 'uppercase',
                    letterSpacing: '.03em',
                    flexShrink: 0,
                    cursor: 'pointer',
                  }}
                  title="Clique para entender o status de Cadastro Escolar"
                >
                  Cadastro Escolar
                </span>

                {/* Actions: Apenas o Olho para abrir ficha completa */}
                <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button
                    className="action-btn"
                    onClick={() => setSelectedAlunoId(a.id)}
                    title="Abrir Ficha e Detalhes do Aluno"
                  >
                    <Eye size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Informativo: O que significa Cadastro Escolar */}
      <CadastroEscolarInfoModal
        open={showInfoCadastroEscolar}
        onClose={() => setShowInfoCadastroEscolar(false)}
        tipo="aluno"
      />

      {/* Modal Ficha Completa do Aluno (Renderizado no body via Portal ocupando toda a tela com margem) */}
      {selectedAlunoId && typeof document !== 'undefined' && createPortal(
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedAlunoId(null);
              fetchAlunos();
            }
          }}
        >
          <div
            style={{
              width: '96vw',
              maxWidth: 1280,
              height: '92vh',
              maxHeight: 900,
              background: '#ffffff',
              borderRadius: 14,
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <AlunoFichaDesktop
              alunoId={selectedAlunoId}
              escolaId={escolaId}
              canManage={true}
              onClose={() => {
                setSelectedAlunoId(null);
                fetchAlunos();
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function EscolasPage() {
  const loc = useLocation();
  const { data, isLoading, create, update, deactivate, refetch } = useCrud<Escola>('escolas');
  const devSettings = useDevStore((s) => s.settings);
  const user = useAuthStore((s) => s.user);
  const isEstadual = user?.perfil === 'ESTADUAL';
  const userEstadoId = user?.estadoId;
  const canCreateEscola = isEstadual ? hasPermission(user, 'escola.manage') : true;
  const canChangeEsfera = isEstadual ? hasPermission(user, 'escola.alterar_esfera' as any) || user?.perfil === 'ADMIN' : true;
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [credenciaisEscola, setCredenciaisEscola] = useState<{ id: string; nome: string } | null>(null);

  const [editing, setEditing] = useState<Escola | null>(null);
  const [step, setStep] = useState(1);
  const [editTab, setEditTab] = useState<'dados' | 'equipe' | 'professores' | 'responsaveis' | 'alunos'>('dados');
  const [equipeCargo, setEquipeCargo] = useState<string>('DIRETOR');
  const [saving, setSaving] = useState(false);

  // Pesquisa
  const [catalogoBusca, setCatalogoBusca] = useState('');
  const [catalogoResults, setCatalogoResults] = useState<CatalogoEscola[]>([]);
  const [catalogoLoading, setCatalogoLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Hierarquia
  const [redes, setRedes] = useState<Rede[]>([]);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [allEstados, setAllEstados] = useState<Estado[]>([]);
  const [allMunicipios, setAllMunicipios] = useState<Municipio[]>([]);

  // Inline creation
  const [showCriarEstado, setShowCriarEstado] = useState(false);
  const [showCriarMunicipio, setShowCriarMunicipio] = useState(false);
  const [criarEstadoLoading, setCriarEstadoLoading] = useState(false);
  const [criarMunicipioLoading, setCriarMunicipioLoading] = useState(false);
  const [criarMunicipioNome, setCriarMunicipioNome] = useState('');
  const [hierarquiaResolvida, setHierarquiaResolvida] = useState(false);
  const [codigoGerado, setCodigoGerado] = useState('');
  const [copyingActivationFor, setCopyingActivationFor] = useState<string | null>(null);

  // Gestores
  const [allUsuarios, setAllUsuarios] = useState<any[]>([]);
  const [gestorInfo, setGestorInfo] = useState<{ nome?: string; email?: string; existe: boolean }>({ existe: false });

  // Formulário escola
  const [form, setForm] = useState({
    nome: '', tipoRede: 'MUNICIPAL', endereco: '', cidade: '', uf: 'MG',
    cep: '', telefone: '', email: '', codigoINEP: '', municipioId: '',
    redeId: '', estadoId: '', logradouro: '', numero: '', bairro: '', complemento: '',
    latitude: '', longitude: '',
  });

  // Geocoding e Localização
  const [geocodingLoading, setGeocodingLoading] = useState(false);

  // Validação assíncrona de INEP em duplicidade
  const [inepError, setInepError] = useState<string | null>(null);
  const [inepValidating, setInepValidating] = useState(false);

  const validarInep = async (inep: string, excludeId?: string): Promise<boolean> => {
    const clean = inep.trim();
    if (!clean) {
      setInepError(null);
      return true;
    }
    setInepValidating(true);
    try {
      const res = await api.get(ROUTES.escolas.checkInep(clean, excludeId));
      if (res.data?.emUso) {
        const esc = res.data.escola;
        const msg = `Código INEP ${clean} já está em uso por "${esc.nome}" (Situação: ${esc.situacao} · ${esc.cidade}/${esc.uf})`;
        setInepError(msg);
        toast.error(msg, { duration: 6000 });
        return false;
      }
      setInepError(null);
      return true;
    } catch {
      return true;
    } finally {
      setInepValidating(false);
    }
  };

  const handleGetDeviceLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocalização não suportada pelo seu navegador.');
      return;
    }
    toast.loading('Obtendo localização do dispositivo...', { id: 'gps-escola' });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          latitude: String(pos.coords.latitude.toFixed(6)),
          longitude: String(pos.coords.longitude.toFixed(6)),
        }));
        toast.success('Localização do dispositivo obtida!', { id: 'gps-escola' });
      },
      (err) => {
        toast.error(`Falha ao obter localização: ${err.message}`, { id: 'gps-escola' });
      },
      { timeout: 8000, enableHighAccuracy: true }
    );
  };

  const handleGetIpLocation = async () => {
    toast.loading('Obtendo localização por IP...', { id: 'ip-escola' });
    try {
      const res = await fetch('https://ipapi.co/json/');
      if (res.ok) {
        const data = await res.json();
        setForm((f) => ({
          ...f,
          latitude: String(parseFloat(data.latitude).toFixed(6)),
          longitude: String(parseFloat(data.longitude).toFixed(6)),
        }));
        toast.success('Localização por IP obtida!', { id: 'ip-escola' });
      } else {
        toast.error('Falha ao buscar geolocalização por IP.', { id: 'ip-escola' });
      }
    } catch (err: any) {
      toast.error(`Erro ao consultar IP: ${err.message}`, { id: 'ip-escola' });
    }
  };

  const handleGeocodeEscolaManual = async (silent = false) => {
    const enderecoBase = form.logradouro.trim() || form.endereco.trim();
    const parts = [
      enderecoBase,
      form.numero.trim(),
      form.bairro.trim(),
      form.cidade.trim(),
      form.uf.trim(),
      'Brasil'
    ].filter(Boolean);

    if (parts.length < 2) {
      if (!silent) toast.error('Preencha ao menos Cidade e UF ou Logradouro para localizar.');
      return;
    }

    const query = parts.join(', ');
    if (!silent) toast.loading('Buscando localização da escola no mapa...', { id: 'geocode-escola' });
    setGeocodingLoading(true);

    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=br`,
        { headers: { 'Accept-Language': 'pt-BR' } }
      );
      if (!r.ok) throw new Error('Erro na consulta de geolocalização');
      const data = await r.json();
      if (data && data.length > 0) {
        setForm((f) => ({
          ...f,
          latitude: String(parseFloat(data[0].lat).toFixed(6)),
          longitude: String(parseFloat(data[0].lon).toFixed(6)),
        }));
        if (!silent) toast.success('Coordenadas localizadas no mapa!', { id: 'geocode-escola' });
      } else {
        // Fallback: tentar somente com Cidade, UF se a busca completa falhou
        if (form.cidade && form.uf) {
          const fallbackQuery = `${form.cidade}, ${form.uf}, Brasil`;
          const r2 = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fallbackQuery)}&format=json&limit=1&countrycodes=br`,
            { headers: { 'Accept-Language': 'pt-BR' } }
          );
          const data2 = await r2.json();
          if (data2 && data2.length > 0) {
            setForm((f) => ({
              ...f,
              latitude: String(parseFloat(data2[0].lat).toFixed(6)),
              longitude: String(parseFloat(data2[0].lon).toFixed(6)),
            }));
            if (!silent) toast.info('Localizado pelo município. Ajuste o pino no mapa se desejar.', { id: 'geocode-escola' });
            return;
          }
        }
        if (!silent) toast.error('Endereço não localizado automaticamente. Informe a Latitude e Longitude ou clique no mapa.', { id: 'geocode-escola' });
      }
    } catch (err: any) {
      if (!silent) toast.error(`Falha na busca: ${err.message}`, { id: 'geocode-escola' });
    } finally {
      setGeocodingLoading(false);
    }
  };

  // Formulario diretor e onboarding com persistência do padrão (Fallback inicial: SETE_TELAS_LIMPO)
  const [diretor, setDiretor] = useState({ nome: '', email: '', telefone: '' });
  const [modoPadraoSalvo, setModoPadraoSalvo] = useState<'SETE_TELAS_LIMPO' | 'SETE_TELAS_DEMO' | 'SOMENTE_EQUIPE'>(() => {
    return (localStorage.getItem('ac_onboarding_default_mode') as any) || 'SETE_TELAS_LIMPO';
  });
  const [opcaoOnboarding, setOpcaoOnboarding] = useState<'SETE_TELAS_LIMPO' | 'SETE_TELAS_DEMO' | 'SOMENTE_EQUIPE'>(modoPadraoSalvo);
  const [acessoDireto, setAcessoDireto] = useState(modoPadraoSalvo === 'SOMENTE_EQUIPE');

  // Link de ativacao gerado
  const [credenciais, setCredenciais] = useState<{
    email: string; activationLink: string; emailEnviado?: boolean;
    emailMessageId?: string | null; emailError?: string | null;
    telefone?: string; nomeDiretor?: string;
  } | null>(null);
  const [showSenha, setShowSenha] = useState(false);
  const [viewMode, setViewMode] = useState<'lista' | 'cards'>(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 'cards';
    }
    return 'lista';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filtroCidade, setFiltroCidade] = useState('');
  const [filtroSetup, setFiltroSetup] = useState<'todos' | 'atividade' | 'implantacao' | 'sem-inicio' | 'desativadas'>('todos');
  const [exibirDemo, setExibirDemo] = useState(false);
  const [pageSize, setPageSize] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState<Escola | null>(null);
  const [escolaToHardDelete, setEscolaToHardDelete] = useState<Escola | null>(null);

  useEffect(() => {
    if (isEstadual) setExibirDemo(false);
  }, [isEstadual]);

  useEffect(() => {
    api.get(ROUTES.admin.redes).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      const redesAtivas = list.filter((x: any) => x.ativo !== false);
      redesAtivas.sort((a: any, b: any) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
      setRedes(redesAtivas);
    }).catch(() => {});
    // Carregar todos estados/municipios para auto-resolução
    api.get(ROUTES.admin.estados).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      const estadosAtivos = list.filter((x: any) => x.ativo !== false);
      estadosAtivos.sort((a: any, b: any) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
      setAllEstados(estadosAtivos);
    }).catch(() => {});
    api.get(ROUTES.admin.municipios).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      const munAtivos = list.filter((x: any) => x.ativo !== false);
      munAtivos.sort((a: any, b: any) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
      setAllMunicipios(munAtivos);
    }).catch(() => {});
    // Carregar usuários para detecção de gestores
    api.get(ROUTES.admin.usuarios).then(r => {
      const list = Array.isArray(r.data) ? r.data : [];
      setAllUsuarios(list.filter((x: any) => x.ativo !== false));
    }).catch(() => {});
  }, []);

  // Detectar gestor com base no tipoRede + hierarquia selecionada
  useEffect(() => {
    if (form.tipoRede === 'PARTICULAR') {
      setGestorInfo({ nome: 'Diretor da Escola', existe: true });
      return;
    }
    if (form.tipoRede === 'MUNICIPAL' && form.municipioId) {
      const gestor = allUsuarios.find(u => u.municipioId === form.municipioId && u.perfil === 'MUNICIPAL');
      setGestorInfo(gestor ? { nome: gestor.nome, email: gestor.email, existe: true } : { existe: false });
      return;
    }
    if (form.tipoRede === 'ESTADUAL' && form.estadoId) {
      const gestor = allUsuarios.find(u => u.estadoId === form.estadoId && u.perfil === 'ESTADUAL');
      setGestorInfo(gestor ? { nome: gestor.nome, email: gestor.email, existe: true } : { existe: false });
      return;
    }
    setGestorInfo({ existe: false });
  }, [form.tipoRede, form.municipioId, form.estadoId, allUsuarios]);

  useEffect(() => {
    if (!form.redeId) { setEstados([]); return; }
    api.get(ROUTES.admin.estadosPorRede(form.redeId)).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      const estadosAtivos = list.filter((x: any) => x.ativo !== false);
      estadosAtivos.sort((a: any, b: any) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
      setEstados(estadosAtivos);
      // Auto-seleciona se houver apenas 1 estado na rede
      if (!isEstadual && estadosAtivos.length === 1) {
        setForm(prev => ({ ...prev, estadoId: estadosAtivos[0].id }));
      }
    }).catch(() => setEstados([]));
  }, [form.redeId, isEstadual]);

  useEffect(() => {
    if (!form.estadoId) { setMunicipios([]); return; }
    api.get(ROUTES.admin.municipiosPorEstado(form.estadoId)).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
      const munAtivos = list.filter((x: any) => x.ativo !== false);
      munAtivos.sort((a: any, b: any) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
      setMunicipios(munAtivos);
      // Auto-seleciona se houver apenas 1 município no estado
      if (munAtivos.length === 1) {
        setForm(prev => ({ ...prev, municipioId: munAtivos[0].id }));
      }
    }).catch(() => setMunicipios([]));
  }, [form.estadoId]);

  // Auto-seleciona a rede se houver apenas 1 cadastrada no sistema
  useEffect(() => {
    if (!isEstadual && redes.length === 1 && !form.redeId) {
      setForm(prev => ({ ...prev, redeId: redes[0].id }));
    }
  }, [redes, isEstadual, form.redeId]);

  const resetForm = () => {
    setStep(1); setCatalogoBusca(''); setCatalogoResults([]); setExpandedGroups({}); setCredenciais(null); setShowSenha(false);
    setShowCriarEstado(false); setShowCriarMunicipio(false); setHierarquiaResolvida(false); setCodigoGerado('');
    setOpcaoOnboarding(modoPadraoSalvo);
    setAcessoDireto(modoPadraoSalvo === 'SOMENTE_EQUIPE');
    setCriarMunicipioNome('');
    setInepError(null);
    const defaultTipoRede = isEstadual ? 'ESTADUAL' : 'MUNICIPAL';
    const defaultEstadoId = isEstadual ? (userEstadoId || '') : '';
    const matchingEstado = isEstadual ? allEstados.find(e => e.id === userEstadoId) : null;
    const defaultRedeId = isEstadual
      ? (matchingEstado?.redeId || user?.redeId || '')
      : (redes.length === 1 ? redes[0].id : '');
    setForm({
      nome: '', tipoRede: defaultTipoRede, endereco: '', cidade: '', uf: matchingEstado?.sigla || 'MG',
      cep: '', telefone: '', email: '', codigoINEP: '', municipioId: '',
      redeId: defaultRedeId, estadoId: defaultEstadoId, logradouro: '', numero: '', bairro: '', complemento: '',
      latitude: '', longitude: ''
    });
    setDiretor({ nome: '', email: '', telefone: '' });
  };

  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Detectar se tem dados preenchidos no formulário
  const hasFormData = () => {
    if (credenciais) return false; // Tela de sucesso pode fechar livre
    if (editing) return false; // Edição usa outro fluxo
    return !!(form.nome.trim() || form.endereco.trim() || form.logradouro.trim() || form.codigoINEP.trim() || diretor.nome.trim() || diretor.email.trim() || step > 1);
  };

  const handleCancelClick = () => {
    if (hasFormData()) {
      setDiscardConfirmOpen(true);
    } else {
      setModalOpen(false);
    }
  };

  const handleConfirmDiscard = () => {
    setDiscardConfirmOpen(false);
    setModalOpen(false);
  };

  const openNew = () => {
    setEditing(null);
    resetForm();
    if (isEstadual && userEstadoId) {
      const matchingEstado = allEstados.find(e => e.id === userEstadoId);
      const defaultRedeId = matchingEstado?.redeId || user?.redeId || '';
      const defaultTipoRede = 'ESTADUAL';
      if (matchingEstado) setEstados([matchingEstado]);
      if (userEstadoId) {
        api.get(ROUTES.admin.municipiosPorEstado(userEstadoId)).then(r => {
          const list = Array.isArray(r.data) ? r.data : r.data.data ?? [];
          setMunicipios(list.filter((x: any) => x.ativo === true));
        }).catch(() => setMunicipios([]));
      }
      setForm({
        nome: '', tipoRede: defaultTipoRede, endereco: '', cidade: '', uf: matchingEstado?.sigla || 'MG',
        cep: '', telefone: '', email: '', codigoINEP: '', municipioId: '',
        redeId: defaultRedeId, estadoId: userEstadoId, logradouro: '', numero: '', bairro: '', complemento: '',
        latitude: '', longitude: '',
      });
    }
    setModalOpen(true);
  };
  const openEdit = (e: Escola) => {
    setEditing(e);
    setStep(2); // Skip catálogo na edição
    setEditTab('dados');
    setEquipeCargo('DIRETOR');
    const estadoIdFound = e.municipio?.estado?.id || (e.municipio as any)?.estadoId || allEstados.find(est => est.sigla === e.uf)?.id || '';
    const redeIdFound = (e.municipio?.estado as any)?.rede?.id || (e.municipio?.estado as any)?.redeId || (e as any).redeId || allEstados.find(est => est.id === estadoIdFound)?.redeId || '';
    setForm({
      nome: e.nome, tipoRede: e.tipoRede, endereco: e.endereco || '',
      cidade: e.cidade || '', uf: e.uf || 'MG', cep: (e as any).cep || '', telefone: e.telefone || '',
      email: e.email || '', codigoINEP: e.codigoINEP || '', municipioId: e.municipioId || '',
      redeId: redeIdFound, estadoId: estadoIdFound, logradouro: e.endereco || '', numero: '', bairro: '', complemento: '',
      latitude: e.latitude != null ? String(e.latitude) : '',
      longitude: e.longitude != null ? String(e.longitude) : '',
    });
    setModalOpen(true);
  };

  useEffect(() => {
    if (loc.state?.openNew && !modalOpen && redes.length > 0) {
      openNew();
      navigate(loc.pathname, { replace: true, state: {} });
    }
  }, [loc.state, redes, modalOpen, navigate]);

  // Buscar no catálogo
  const buscarCatalogo = useCallback(async () => {
    if (catalogoBusca.length < 3) { toast.error('Digite pelo menos 3 caracteres'); return; }
    setCatalogoLoading(true);
    try {
      const { data: resp } = await api.get(ROUTES.catalogo.busca(catalogoBusca));
      setCatalogoResults(resp.data || []);
      setExpandedGroups({});
      if ((resp.data || []).length === 0) toast.info('Nenhuma escola encontrada no catálogo');
    } catch { toast.error('Erro na busca'); }
    finally { setCatalogoLoading(false); }
  }, [catalogoBusca]);

  const selecionarCatalogo = (c: CatalogoEscola) => {
    const dep = c.dependenciaAdministrativa?.toUpperCase() || '';
    let tipoRede = 'MUNICIPAL';
    if (dep.includes('ESTADUAL')) tipoRede = 'ESTADUAL';
    else if (dep.includes('PRIVAD') || dep.includes('PARTICULAR')) tipoRede = 'PARTICULAR';

    // Auto-resolução da hierarquia
    const estadoEncontrado = allEstados.find(e => e.sigla === c.uf);
    let redeId = '', estadoId = '', municipioId = '';
    if (estadoEncontrado) {
      redeId = estadoEncontrado.redeId || (redes.find(r => allEstados.some(e => e.sigla === c.uf && e.redeId === r.id))?.id || '');
      estadoId = estadoEncontrado.id;
      // Buscar município por nome dentro do estado
      const munEncontrado = allMunicipios.find(m => m.estadoId === estadoId && m.nome.toUpperCase() === c.municipio.toUpperCase());
      if (munEncontrado) municipioId = munEncontrado.id;
    }

    setForm(prev => ({
      ...prev,
      nome: c.nome, endereco: c.endereco || '', cidade: c.municipio, uf: c.uf,
      telefone: c.telefone || '', codigoINEP: c.codigoInep, tipoRede,
      redeId, estadoId, municipioId, logradouro: c.endereco || '', numero: '', bairro: '', complemento: '',
      latitude: c.latitude != null ? String(c.latitude) : '',
      longitude: c.longitude != null ? String(c.longitude) : '',
    }));
    setShowCriarEstado(false); setShowCriarMunicipio(false);
    setCriarMunicipioNome(c.municipio);
    setHierarquiaResolvida(!!estadoEncontrado);
    setStep(1);
    toast.success('Dados do catálogo preenchidos!');
  };

  // Criação inline de Estado
  const handleCriarEstado = async () => {
    if (!form.uf || form.uf.length !== 2) { toast.error('UF inválida'); return; }
    if (redes.length === 0) { toast.error('Nenhuma rede cadastrada'); return; }
    const redeId = form.redeId || redes[0].id;
    setCriarEstadoLoading(true);
    try {
      const NOMES_UF: Record<string, string> = {
        AC:'Acre',AL:'Alagoas',AP:'Amapá',AM:'Amazonas',BA:'Bahia',CE:'Ceará',DF:'Distrito Federal',
        ES:'Espírito Santo',GO:'Goiás',MA:'Maranhão',MT:'Mato Grosso',MS:'Mato Grosso do Sul',
        MG:'Minas Gerais',PA:'Pará',PB:'Paraíba',PR:'Paraná',PE:'Pernambuco',PI:'Piauí',
        RJ:'Rio de Janeiro',RN:'Rio Grande do Norte',RS:'Rio Grande do Sul',RO:'Rondônia',
        RR:'Roraima',SC:'Santa Catarina',SP:'São Paulo',SE:'Sergipe',TO:'Tocantins',
      };
      const { data: novoEstado } = await api.post(ROUTES.admin.estados, {
        redeId, nome: NOMES_UF[form.uf] || form.uf, sigla: form.uf,
      });
      setAllEstados(prev => [...prev, novoEstado]);
      // Refresh cascading dropdown
      setEstados(prev => [...prev, novoEstado]);
      setForm(prev => ({ ...prev, redeId, estadoId: novoEstado.id }));
      setShowCriarEstado(false);
      toast.success(`Estado ${form.uf} criado!`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao criar estado');
    } finally { setCriarEstadoLoading(false); }
  };

  // Criação inline de Município
  const handleCriarMunicipio = async () => {
    if (!form.estadoId) { toast.error('Selecione um estado primeiro'); return; }
    const nome = criarMunicipioNome.trim() || form.cidade.trim();
    if (!nome) { toast.error('Nome do município é obrigatório'); return; }
    setCriarMunicipioLoading(true);
    try {
      const { data: novoMun } = await api.post(ROUTES.admin.municipios, {
        estadoId: form.estadoId, nome,
      });
      setAllMunicipios(prev => [...prev, novoMun]);
      // Refresh cascading dropdown
      setMunicipios(prev => [...prev, novoMun]);
      setForm(prev => ({ ...prev, municipioId: novoMun.id }));
      setShowCriarMunicipio(false);
      toast.success(`Município ${nome} criado!`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao criar município');
    } finally { setCriarMunicipioLoading(false); }
  };

  const handleSave = async () => {
    const enderecoCompleto = `${form.logradouro}${form.numero ? `, ${form.numero}` : ''}${form.bairro ? ` - ${form.bairro}` : ''}${form.complemento ? ` (${form.complemento})` : ''}`.trim();

    // Validar dados principais
    if (!form.nome.trim()) { toast.error('Nome e obrigatorio'); return; }
    if (!editing) {
      if (!form.cep.trim() || !form.logradouro.trim() || !form.numero.trim() || !form.bairro.trim() || !form.cidade.trim() || !form.uf.trim()) {
        toast.error('Preencha o endereço completo (CEP, logradouro, número, bairro, cidade e UF)');
        return;
      }
    }
    if (!enderecoCompleto && !form.endereco.trim()) { toast.error('Endereco e obrigatorio'); return; }

    // Validar vínculo para escolas públicas
    if (form.tipoRede === 'MUNICIPAL' && !form.municipioId && !editing?.municipioId) {
      toast.error('Escolas municipais precisam estar vinculadas a um município'); return;
    }
    if (form.tipoRede === 'ESTADUAL') {
      const estadoIdCandidate = form.estadoId || (editing?.municipio as any)?.estadoId || allEstados.find(est => est.sigla === (form.uf || editing?.uf))?.id;
      if (!estadoIdCandidate && !editing?.uf) {
        toast.error('Escolas estaduais precisam estar vinculadas a um estado'); return;
      }
    }

    if (form.codigoINEP?.trim()) {
      const inepOk = await validarInep(form.codigoINEP, editing?.id);
      if (!inepOk) return;
    }

    if (editing) {
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        toast.error('Email institucional da escola inválido');
        return;
      }
      setSaving(true);
      try {
        await update.mutateAsync({
          id: editing.id,
          nome: form.nome,
          tipoRede: form.tipoRede,
          endereco: enderecoCompleto || form.endereco,
          cidade: form.cidade,
          uf: form.uf,
          cep: form.cep,
          telefone: form.telefone,
          email: form.email,
          codigoINEP: form.codigoINEP,
          municipioId: form.municipioId || undefined,
          latitude: form.latitude ? parseFloat(form.latitude) : null,
          longitude: form.longitude ? parseFloat(form.longitude) : null,
        } as any);
        toast.success('Escola atualizada!');
        setModalOpen(false);
      } catch (e: any) { toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao salvar'); }
      finally { setSaving(false); }
      return;
    }

    // Validar Step 3 (diretor)
    const diretorNome = (diretor.nome || '').trim();
    const diretorEmail = (diretor.email || '').trim();
    if (!diretorNome || !diretorEmail) {
      toast.error('Nome e Email do diretor sao obrigatorios');
      return;
    }
    // Validar formato de email
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(diretorEmail)) {
      toast.error('Email do diretor invalido');
      return;
    }
    // Verificar se email do diretor ja esta em uso
    const emailDuplicado = allUsuarios.find(u => u.email?.toLowerCase() === diretorEmail.toLowerCase());
    if (emailDuplicado) {
      toast.error(`Este e-mail ja esta em uso por: ${emailDuplicado.nome} (${emailDuplicado.perfil})`);
      return;
    }

    setSaving(true);
    try {
      // 1. Criar escola
      const escolaPayload: any = {
        nome: form.nome, tipoRede: form.tipoRede,
        endereco: enderecoCompleto || form.endereco,
        cidade: form.cidade || municipios.find(m => m.id === form.municipioId)?.nome || '',
        uf: form.uf || 'MG',
        cep: form.cep || undefined, nomeDiretor: diretor.nome,
        codigoINEP: form.codigoINEP || undefined,
        municipioId: form.municipioId || undefined,
        latitude: form.latitude ? parseFloat(form.latitude) : undefined,
        longitude: form.longitude ? parseFloat(form.longitude) : undefined,
        opcaoOnboarding,
        isDemo: false,
      };
      const { data: novaEscola } = await api.post(ROUTES.escolas.listar, escolaPayload);

      // 2. Convidar diretor via link de ativacao
      let linkAtivacao = '';
      let emailEnviado = false;
      let emailMsgId: string | null = null;
      let emailErr: string | null = null;
      try {
        const { data: convite } = await api.post(ROUTES.equipe.convidar(novaEscola.id), {
          nome: diretor.nome,
          email: diretor.email,
          telefone: diretor.telefone || undefined,
          cargo: 'DIRETOR',
          acessoDireto,
        });
        linkAtivacao = convite.activationLink || '';
        emailEnviado = convite.emailEnviado === true;
        emailMsgId = convite.emailMessageId || null;
        emailErr = convite.emailError || null;
      } catch (e: any) {
        toast.warning('Escola criada mas erro ao convidar diretor: ' + (e?.response?.data?.error?.message || e?.response?.data?.message || ''));
      }

      // 3. Exibir link de ativacao
      setCodigoGerado(novaEscola.codigo || '');
      setCredenciais({
        email: diretor.email, activationLink: linkAtivacao, emailEnviado,
        emailMessageId: emailMsgId, emailError: emailErr,
        telefone: diretor.telefone || '', nomeDiretor: diretor.nome || '',
      });
      refetch();
      toast.success('Escola criada! Configuração inicial (matérias, turnos, turma) em segundo plano…');

      // Aguarda bootstrap sem bloquear a UI (evita parecer “vazia” logo após criar).
      const escolaId = novaEscola.id as string;
      void (async () => {
        for (let i = 0; i < 45; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          try {
            const { data: st } = await api.get(ROUTES.escolas.bootstrapStatus(escolaId));
            if (st?.status === 'CONCLUIDO') {
              toast.success('Configuração inicial da escola concluída.');
              refetch();
              return;
            }
            if (st?.status === 'ERRO') {
              toast.warning(
                st?.erro
                  ? `Escola criada, mas o setup falhou: ${st.erro}`
                  : 'Escola criada, mas o setup inicial falhou. Tente reabrir a escola.',
              );
              return;
            }
          } catch {
            // rede instável — segue tentando
          }
        }
      })();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao criar escola');
    } finally { setSaving(false); }
  };

  const requestDelete = (e: Escola) => {
    setDeleteConfirm(e);
  };
  const confirmDeleteEscola = async () => {
    if (!deleteConfirm) return;
    const id = deleteConfirm.id;
    setDeleteConfirm(null);
    try {
      await deactivate.mutateAsync(id);
      toast.success('Escola desativada');
      refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.message || 'Erro ao desativar escola';
      toast.error(msg);
    }
  };

  const handleActivateEscola = async (e: Escola) => {
    try {
      await update.mutateAsync({ id: e.id, ativo: true } as any);
      toast.success('Escola ativada com sucesso!');
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao ativar escola');
    }
  };

  const handleHardDeleteEscola = (e: Escola) => {
    setEscolaToHardDelete(e);
  };

  const confirmHardDeleteEscola = async () => {
    if (!escolaToHardDelete) return;
    const e = escolaToHardDelete;
    setEscolaToHardDelete(null);
    try {
      await api.delete(`/escolas/hard/${e.id}`);
      toast.success('Escola removida com sucesso!');
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao remover escola permanentemente');
    }
  };

  const copiarCredenciais = () => {
    if (!credenciais) return;
    const activationLink = normalizeActivationLink(credenciais.activationLink);
    navigator.clipboard.writeText(activationLink || credenciais.email);
    toast.success(activationLink ? 'URL de ativacao copiada!' : 'Email copiado!');
  };

  const scopedEscolas = useMemo(() => {
    const all = (data as Escola[]) || [];
    if (isEstadual && userEstadoId) {
      return all.filter((e: Escola) => {
        if (e.isDemo) return false;
        if (e.tipoRede !== 'ESTADUAL') return false;
        const munMatch =
          e.municipio?.estado?.id === userEstadoId ||
          (e.municipio as any)?.estadoId === userEstadoId ||
          allMunicipios.some((m) => m.id === e.municipioId && m.estadoId === userEstadoId);
        return munMatch;
      });
    }
    return all;
  }, [data, isEstadual, userEstadoId, allMunicipios]);

  const cidadesDisponiveis = useMemo(() => {
    const unique = new Set(
      scopedEscolas.map((e: Escola) => e.municipio?.nome || e.cidade || '').filter(Boolean)
    );
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [scopedEscolas]);

  const getSetupStage = (e: Escola) => {
    if (e.ativo === false) return 'desativadas';
    if (e.wizardConcluido === true) return 'atividade';

    const turmas = e._count?.turmas || 0;
    const professores = e._count?.professorEscolas || 0;
    const alunos = e._count?.alunos || 0;
    const etapaAtual = Number(e.wizardEtapaAtual || 0);

    // Concluída / Em Atividade: quando todas as etapas essenciais foram configuradas com alunos e turmas
    if (turmas > 0 && professores > 0 && alunos > 0 && etapaAtual >= 7) {
      return 'atividade';
    }

    // Em Implantação / Configuração: qualquer cadastro intermediário sem ter concluído
    if (etapaAtual > 0 || turmas > 0 || professores > 0 || alunos > 0) {
      return 'implantacao';
    }

    return 'sem-inicio';
  };

  const totalDemoEscolas = useMemo(() => {
    return isEstadual ? 0 : scopedEscolas.filter(e => e.isDemo || e.nome.toLowerCase().includes('demo')).length;
  }, [scopedEscolas, isEstadual]);

  const setupCounts = useMemo(() => {
    const base = scopedEscolas.filter((e: Escola) => {
      const isDemoSchool = e.isDemo || e.nome.toLowerCase().includes('demo');
      if ((!exibirDemo || isEstadual) && isDemoSchool) return false;
      return true;
    });

    const ativas = base.filter(e => e.ativo !== false);
    const desativadas = base.filter(e => e.ativo === false);

    return {
      todos: ativas.length,
      atividade: ativas.filter(e => getSetupStage(e) === 'atividade').length,
      implantacao: ativas.filter(e => getSetupStage(e) === 'implantacao').length,
      'sem-inicio': ativas.filter(e => getSetupStage(e) === 'sem-inicio').length,
      desativadas: desativadas.length,
    };
  }, [scopedEscolas, exibirDemo, isEstadual]);

  const tabDemoEscolas = useMemo(() => {
    if (isEstadual) return 0;
    const all = scopedEscolas;
    let list = all;
    if (filtroSetup === 'desativadas') {
      list = all.filter(e => e.ativo === false);
    } else {
      list = all.filter(e => e.ativo !== false);
      if (filtroSetup !== 'todos') list = list.filter(e => getSetupStage(e) === filtroSetup);
    }
    return list.filter(e => e.isDemo || e.nome.toLowerCase().includes('demo')).length;
  }, [scopedEscolas, filtroSetup, isEstadual]);

  const escolasFiltradas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return scopedEscolas.filter((e: Escola) => {
      const isDemoSchool = e.isDemo || e.nome.toLowerCase().includes('demo');
      if ((!exibirDemo || isEstadual) && isDemoSchool) return false;

      if (filtroSetup === 'desativadas') {
        if (e.ativo !== false) return false;
      } else {
        if (e.ativo === false) return false;
        if (filtroSetup !== 'todos' && getSetupStage(e) !== filtroSetup) return false;
      }

      const cidade = e.municipio?.nome || e.cidade || '';
      const estado = e.municipio?.estado?.sigla || e.uf || '';
      const matchCidade = !filtroCidade || cidade === filtroCidade;
      const matchBusca = !q || [
        e.nome,
        e.codigo,
        e.tipoRede,
        cidade,
        estado,
        e.wizardConcluido ? 'setup concluido' : `setup etapa ${e.wizardEtapaAtual || 0}`,
      ].join(' ').toLowerCase().includes(q);
      return matchCidade && matchBusca;
    });
  }, [data, searchQuery, filtroCidade, filtroSetup, exibirDemo]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filtroCidade, filtroSetup, viewMode, pageSize]);

  const totalPages = Math.max(1, Math.ceil(escolasFiltradas.length / pageSize));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedEscolas = useMemo(() => {
    const start = (currentPageSafe - 1) * pageSize;
    return escolasFiltradas.slice(start, start + pageSize);
  }, [escolasFiltradas, currentPageSafe, pageSize]);

  const renderTipoBadge = (tipoRede: string) => {
    const colors: Record<string, string> = { MUNICIPAL: '#10b981', ESTADUAL: '#6366f1', PARTICULAR: '#f59e0b' };
    const color = colors[tipoRede] || '#6b7280';
    return (
      <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: '.72rem', fontWeight: 700, background: `${color}20`, color }}>
        {tipoRede}
      </span>
    );
  };

  const renderEquipeBadge = (escola: Escola) => {
    const total = escola._count?.professorEscolas || 0;
    const color = total > 0 ? '#2563eb' : '#94a3b8';
    return (
      <span
        title={`Equipe vinculada: ${total}`}
        style={{
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: '.72rem',
          fontWeight: 700,
          background: `${color}18`,
          color,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Users size={12} />
        {total}
      </span>
    );
  };

  const handleCopyDirectorLink = async (escola: Escola) => {
    setCopyingActivationFor(escola.id);
    try {
      const { data: equipe } = await api.get(ROUTES.equipe.listar(escola.id));
      const list = Array.isArray(equipe) ? equipe : [];
      const diretor = list.find((m: any) => m.cargo === 'DIRETOR') || list.find((m: any) => m.perfil === 'ESCOLA');

      if (!diretor) {
        toast.error('Diretor não encontrado nesta escola');
        return;
      }

      let tokenAtivacao = diretor.tokenAtivacao as string | null;
      let codigoDisplay = diretor.codigoDisplay as string | null;

      if (!tokenAtivacao && diretor.statusConta !== 'ATIVA') {
        const { data: reenviado } = await api.post(ROUTES.auth.resendInvite(diretor.id));
        tokenAtivacao = reenviado?.tokenAtivacao || null;
        codigoDisplay = reenviado?.codigoDisplay || codigoDisplay;
      }

      if (!tokenAtivacao) {
        if (diretor.statusConta === 'ATIVA') toast.info('Diretor já está ativo nesta escola');
        else toast.error('Não foi possível obter token de ativação');
        return;
      }

      const rawLink = codigoDisplay
        ? `${window.location.origin}/ativar?token=${tokenAtivacao}&code=${codigoDisplay}`
        : `${window.location.origin}/ativar?token=${tokenAtivacao}`;
      await navigator.clipboard.writeText(rawLink);
      toast.success('Link de acesso do Diretor copiado para a área de transferência!');
    } catch {
      toast.error('Erro ao copiar link de acesso do Diretor');
    } finally {
      setCopyingActivationFor(null);
    }
  };

  const renderSetupBadge = (escola: Escola) => {
    const stage = getSetupStage(escola);
    const etapaAtual = Number(escola.wizardEtapaAtual || 0);
    const concluido = escola.wizardConcluido === true;

    let label = 'Não iniciado';
    let title = 'Setup inicial ainda não iniciado';
    let color = '#64748b';

    if (stage === 'atividade') {
      label = concluido ? 'Concluído' : 'Em atividade';
      title = escola.wizardConcluidoEm
        ? `Setup concluído em ${new Date(escola.wizardConcluidoEm).toLocaleString('pt-BR')}`
        : `Escola em atividade (${escola._count?.alunos || 0} alunos, ${escola._count?.professorEscolas || 0} professores)`;
      color = '#10b981';
    } else if (stage === 'implantacao') {
      label = etapaAtual > 0 ? `Etapa ${Math.min(etapaAtual, 8)}/8` : 'Em implantação';
      title = etapaAtual > 0 ? `Setup em andamento na etapa ${Math.min(etapaAtual, 8)} de 8` : 'Escola em fase de implantação/configuração';
      color = '#f59e0b';
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
        <span
          title={title}
          style={{
            padding: '2px 8px',
            borderRadius: 999,
            fontSize: '.72rem',
            fontWeight: 700,
            background: `${color}20`,
            color,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <ClipboardList size={12} />
          {label}
        </span>

        {/* Link do Diretor: exibido sempre que a escola NÃO estiver concluída em atividade e puder ser gerenciada */}
        {stage !== 'atividade' && escola.ativo && canManageEscola(escola) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleCopyDirectorLink(escola);
            }}
            disabled={copyingActivationFor === escola.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              background: 'transparent',
              border: 'none',
              padding: '0',
              marginTop: 1,
              fontSize: '.66rem',
              fontWeight: 600,
              color: '#4f46e5',
              cursor: copyingActivationFor === escola.id ? 'wait' : 'pointer',
              textDecoration: 'none',
              opacity: copyingActivationFor === escola.id ? 0.6 : 0.9,
              letterSpacing: '-0.01em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
              e.currentTarget.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
              e.currentTarget.style.opacity = '0.9';
            }}
            title="Copiar link de acesso e ativação do diretor"
          >
            {copyingActivationFor === escola.id ? (
              <Loader2 className="animate-spin" size={10} />
            ) : (
              <Copy size={10} />
            )}
            Link do Diretor
          </button>
        )}
      </div>
    );
  };

  const canManageEscola = (escola: Escola) => {
    if (user?.perfil === 'ADMIN' || user?.perfil === 'REDE') return true;
    if (isEstadual) {
      return escola.tipoRede === 'ESTADUAL';
    }
    return true;
  };

  const renderActions = (escola: Escola) => {
    const podeGerenciar = canManageEscola(escola);
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    if (isMobile) {
      const items: RowActionItem[] = [
        {
          label: 'Ver detalhes',
          icon: <ExternalLink size={15} />,
          variant: 'view',
          onClick: () => navigate(isEstadual ? `/estadual/escolas/${escola.id}` : `/master/escolas/${escola.id}`),
        },
      ];

      if (podeGerenciar) {
        items.push(
          {
            label: 'Editar',
            icon: <Pencil size={15} />,
            variant: 'edit',
            onClick: () => openEdit(escola),
          },
          {
            label: 'Central de Acessos',
            icon: <KeyRound size={15} />,
            variant: 'primary',
            onClick: () => setCredenciaisEscola({ id: escola.id, nome: escola.nome }),
          },
          escola.ativo
            ? {
                label: 'Desativar escola',
                icon: <PowerOff size={15} />,
                variant: 'danger',
                onClick: () => requestDelete(escola),
              }
            : {
                label: 'Ativar escola',
                icon: <Power size={15} />,
                variant: 'success',
                onClick: () => handleActivateEscola(escola),
              }
        );
      }

      return <RowActionMenu items={items} />;
    }

    return (
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          className="action-btn"
          onClick={() => navigate(isEstadual ? `/estadual/escolas/${escola.id}` : `/master/escolas/${escola.id}`)}
          title="Ver detalhes"
        >
          <ExternalLink size={15} />
        </button>
        <button
          className="action-btn"
          onClick={() => podeGerenciar && openEdit(escola)}
          disabled={!podeGerenciar}
          style={!podeGerenciar ? { opacity: 0.35, cursor: 'not-allowed', color: '#94a3b8' } : undefined}
          title={podeGerenciar ? 'Editar' : 'Gestão restrita à esfera municipal (somente visualização)'}
        >
          <Pencil size={15} />
        </button>
        <button
          className="action-btn"
          onClick={() => podeGerenciar && setCredenciaisEscola({ id: escola.id, nome: escola.nome })}
          disabled={!podeGerenciar}
          style={{
            color: podeGerenciar ? '#6366f1' : '#94a3b8',
            opacity: podeGerenciar ? 1 : 0.35,
            cursor: podeGerenciar ? 'pointer' : 'not-allowed',
          }}
          title={podeGerenciar ? 'Central de Acessos, Equipe e Alunos' : 'Gestão restrita à esfera municipal (somente visualização)'}
        >
          <KeyRound size={15} />
        </button>
        {escola.ativo ? (
          <button
            className="action-btn action-btn-danger"
            onClick={() => podeGerenciar && requestDelete(escola)}
            disabled={!podeGerenciar}
            style={{
              color: podeGerenciar ? '#ef4444' : '#94a3b8',
              opacity: podeGerenciar ? 1 : 0.35,
              cursor: podeGerenciar ? 'pointer' : 'not-allowed',
            }}
            title={podeGerenciar ? 'Desativar escola' : 'Gestão restrita à esfera municipal (somente visualização)'}
          >
            <PowerOff size={15} />
          </button>
        ) : (
          <button
            className="action-btn"
            onClick={() => podeGerenciar && handleActivateEscola(escola)}
            disabled={!podeGerenciar}
            style={{
              color: podeGerenciar ? '#10b981' : '#94a3b8',
              border: podeGerenciar ? '1px solid rgba(16,185,129,0.35)' : '1px solid #e2e8f0',
              background: podeGerenciar ? 'rgba(16,185,129,0.1)' : 'transparent',
              opacity: podeGerenciar ? 1 : 0.35,
              cursor: podeGerenciar ? 'pointer' : 'not-allowed',
            }}
            title={podeGerenciar ? 'Ativar escola' : 'Gestão restrita à esfera municipal (somente visualização)'}
          >
            <Power size={15} />
          </button>
        )}
      </div>
    );
  };

  const stepperItems = editing
    ? [{ n: 2, label: 'Dados da Escola' }]
    : [
        { n: 1, label: 'Esfera e Vinculação' },
        { n: 2, label: 'Nome e INEP' },
        { n: 3, label: 'Endereço' },
        { n: 4, label: 'Diretor' },
      ];

  const renderStepper = () => !credenciais && (
    <div style={{ marginBottom: 16, padding: '8px 0 10px', borderBottom: '1px solid var(--border-color)' }}>
      <style>{`
        @keyframes stepPulseCurrent {
          0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(99,102,241,.30); }
          70% { transform: scale(1.06); box-shadow: 0 0 0 8px rgba(99,102,241,0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(99,102,241,0); }
        }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 0, maxWidth: 560, margin: '0 auto' }}>
        {stepperItems.map((s, i) => {
          const isCurrent = step === s.n;
          const isDone = step > s.n;
          const isInactive = step < s.n;
          return (
            <div key={s.n} style={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, minWidth: 64 }}>
                <span
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '.76rem',
                    fontWeight: 800,
                    background: isCurrent ? '#6366f1' : isDone ? '#10b981' : 'var(--bg-main, #f3f4f6)',
                    color: isCurrent || isDone ? '#fff' : 'var(--text-muted)',
                    border: isCurrent ? '2px solid #4f46e5' : '1px solid var(--border-color)',
                    boxShadow: isCurrent ? '0 0 0 3px rgba(99,102,241,.16)' : 'none',
                    transition: 'all .2s ease',
                    animation: isCurrent ? 'stepPulseCurrent 1.8s ease-in-out infinite' : 'none',
                  }}
                >
                  {isDone ? '✓' : s.n}
                </span>
                <span
                  style={{
                    fontSize: '.67rem',
                    lineHeight: 1.2,
                    textAlign: 'center',
                    fontWeight: isCurrent ? 700 : 500,
                    color: isCurrent ? '#4f46e5' : isDone ? 'var(--text-main)' : 'var(--text-muted)',
                    opacity: isInactive ? 0.72 : 1,
                    maxWidth: 110,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {s.label}
                </span>
              </div>
              {i < stepperItems.length - 1 && (
                <div
                  style={{
                    flex: 1,
                    height: 2,
                    margin: '13px 8px 0',
                    background: step > s.n ? '#10b981' : 'var(--border-color)',
                    borderRadius: 999,
                    opacity: step > s.n ? 1 : 0.65,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ── Step 2: Dados da Escola ──────────────────────────────
  const vinculoDefinido = form.tipoRede === 'PARTICULAR'
    || (form.tipoRede === 'MUNICIPAL' && !!form.municipioId)
    || (form.tipoRede === 'ESTADUAL' && !!form.estadoId);

  const renderStep2 = () => {
    if (step === 1) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Banner 1: Nenhuma Rede cadastrada no sistema */}
          {redes.length === 0 && !isEstadual && (
            <div style={{
              padding: '10px 14px',
              borderRadius: 8,
              background: 'rgba(245, 158, 11, 0.09)',
              border: '1px solid rgba(245, 158, 11, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              fontSize: '0.82rem',
              color: '#b45309',
              fontWeight: 600
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                <span>Nenhuma <strong>Rede Mantenedora</strong> cadastrada. Cadastre primeiro uma Rede em Gestão de Redes.</span>
              </div>
              <button
                type="button"
                className="btn-save"
                style={{ padding: '4px 10px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                onClick={() => navigate('/master/redes')}
              >
                Cadastrar Rede →
              </button>
            </div>
          )}

          {/* Banner 2: Rede selecionada, mas sem Estados vinculados */}
          {form.redeId && estados.length === 0 && !isEstadual && (
            <div style={{
              padding: '10px 14px',
              borderRadius: 8,
              background: 'rgba(245, 158, 11, 0.09)',
              border: '1px solid rgba(245, 158, 11, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              fontSize: '0.82rem',
              color: '#b45309',
              fontWeight: 600
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                <span>A <strong>Rede Mantenedora</strong> selecionada não possui nenhum Estado vinculado. Cadastre o Estado.</span>
              </div>
              <button
                type="button"
                className="btn-save"
                style={{ padding: '4px 10px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                onClick={() => navigate('/master/estados')}
              >
                Cadastrar Estado →
              </button>
            </div>
          )}

          {/* Banner 3: Estado selecionado, mas sem Municípios vinculados */}
          {form.estadoId && municipios.length === 0 && (
            <div style={{
              padding: '10px 14px',
              borderRadius: 8,
              background: 'rgba(245, 158, 11, 0.09)',
              border: '1px solid rgba(245, 158, 11, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              fontSize: '0.82rem',
              color: '#b45309',
              fontWeight: 600
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                <span>O <strong>Estado</strong> selecionado não possui nenhum Município cadastrado. Cadastre o Município.</span>
              </div>
              <button
                type="button"
                className="btn-save"
                style={{ padding: '4px 10px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                onClick={() => navigate('/master/municipios')}
              >
                Cadastrar Município →
              </button>
            </div>
          )}

          <div style={{ padding: 12, borderRadius: 10, background: 'rgba(99,102,241,.04)', border: '1px solid rgba(99,102,241,.15)' }}>
            <p className="form-label" style={{ textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, fontSize: '.68rem', fontWeight: 700, color: '#6366f1' }}>
              Classificação e Vinculação Institucional
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  Esfera Administrativa * {isEstadual && !canChangeEsfera && <span style={{ fontSize: '0.68rem', color: '#6366f1', fontWeight: 600 }}>(Fixa)</span>}
                </label>
                <select
                  name="tipoRede"
                  className="form-select"
                  value={form.tipoRede}
                  onChange={e => setForm(prev => ({ ...prev, tipoRede: e.target.value }))}
                  disabled={isEstadual && !canChangeEsfera}
                  style={isEstadual && !canChangeEsfera ? { background: '#f1f5f9', cursor: 'not-allowed', color: '#334155', fontWeight: 600 } : undefined}
                >
                  {TIPO_REDE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Gestor Responsável</label>
                {vinculoDefinido ? (
                  gestorInfo.existe ? <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(16,185,129,.08)', border: '1px solid rgba(16,185,129,.25)', fontSize: '.82rem', fontWeight: 600, color: '#059669' }}>{gestorInfo.nome}</div>
                  : <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', fontSize: '.78rem', fontWeight: 600, color: '#dc2626' }}>Sem gestor cadastrado</div>
                ) : <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--bg-muted, #f1f4f8)', fontSize: '.78rem', color: '#9ca3af' }}>Defina o vínculo abaixo...</div>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  Rede Mantenedora * {isEstadual && <span style={{ fontSize: '0.68rem', color: '#6366f1', fontWeight: 600 }}>(Fixa)</span>}
                </label>
                <select
                  name="redeId"
                  className="form-select"
                  value={form.redeId}
                  onChange={e => !isEstadual && setForm(prev => ({ ...prev, redeId: e.target.value, estadoId: '', municipioId: '' }))}
                  disabled={isEstadual}
                  style={isEstadual ? { background: '#f1f5f9', cursor: 'not-allowed', color: '#334155', fontWeight: 600 } : undefined}
                >
                  <option value="">{redes.length === 0 ? 'Nenhuma rede cadastrada' : 'Selecione a rede...'}</option>
                  {redes.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">
                  Estado * {isEstadual && <span style={{ fontSize: '0.68rem', color: '#6366f1', fontWeight: 600 }}>(Fixo)</span>}
                </label>
                <select
                  name="estadoId"
                  className="form-select"
                  value={form.estadoId}
                  onChange={e => !isEstadual && setForm(prev => ({ ...prev, estadoId: e.target.value, municipioId: '' }))}
                  disabled={isEstadual || !form.redeId}
                  style={isEstadual ? { background: '#f1f5f9', cursor: 'not-allowed', color: '#334155', fontWeight: 600 } : undefined}
                >
                  <option value="">
                    {!form.redeId
                      ? '—'
                      : estados.length === 0
                      ? 'Nenhum estado cadastrado nesta rede'
                      : 'Selecione o estado...'}
                  </option>
                  {(isEstadual && allEstados.length > 0 ? allEstados.filter(e => e.id === userEstadoId) : estados).map(e => (
                    <option key={e.id} value={e.id}>{e.nome} ({(e as any).sigla})</option>
                  ))}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Município *</label>
                <select
                  name="municipioId"
                  className="form-select"
                  value={form.municipioId}
                  onChange={e => setForm(prev => ({ ...prev, municipioId: e.target.value }))}
                  disabled={!form.estadoId}
                >
                  <option value="">
                    {!form.estadoId
                      ? '—'
                      : municipios.length === 0
                      ? 'Nenhum município cadastrado neste estado'
                      : 'Selecione o município...'}
                  </option>
                  {municipios.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Busca INEP desativada temporariamente (pode ser reativada depois).
              Motivo: no fluxo atual estava gerando mais confusão do que ajuda para o usuário. */}
          {false && (
            <div style={{ padding: 10, borderRadius: 10, background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)' }}>
              <p style={{ fontSize: '.74rem', color: 'var(--text-muted)', marginBottom: 6 }}>Opcional: busque por nome ou código INEP para auto-preencher.</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-input" style={{ flex: 1 }} value={catalogoBusca} onChange={e => setCatalogoBusca(e.target.value)} placeholder="Nome da escola ou código INEP..." onKeyDown={e => e.key === 'Enter' && buscarCatalogo()} />
                <button className="btn-primary" onClick={buscarCatalogo} disabled={catalogoLoading} style={{ whiteSpace: 'nowrap' }}><Search size={16} /> {catalogoLoading ? 'Buscando...' : 'Buscar'}</button>
              </div>
              {catalogoResults.length > 0 && (
                <div style={{ marginTop: 6, maxHeight: 110, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 8 }}>
                  {catalogoResults.slice(0, 20).map(c => (
                    <div key={c.id} onClick={() => selecionarCatalogo(c)} style={{ padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ fontWeight: 600, fontSize: '.78rem' }}>{c.nome}</div>
                      <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginTop: 1 }}>INEP: {c.codigoInep} · {c.municipio}/{c.uf}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Nome da Escola *</label>
              <input
                className="form-input"
                name="nome"
                data-testid="input-escola-nome"
                value={form.nome}
                onChange={e => setForm(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: Escola Municipal Monteiro Lobato"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Código INEP</label>
              <input
                className="form-input"
                style={inepError ? { borderColor: '#ef4444', background: 'rgba(239,68,68,.04)' } : {}}
                name="inep"
                data-testid="input-escola-inep"
                value={form.codigoINEP}
                onChange={e => {
                  const v = e.target.value;
                  setForm(prev => ({ ...prev, codigoINEP: v }));
                  if (inepError) setInepError(null);
                }}
                onBlur={() => {
                  if (form.codigoINEP.trim()) {
                    validarInep(form.codigoINEP, editing?.id);
                  }
                }}
                placeholder="Ex: 31000000"
              />
            </div>
          </div>
          {inepError && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '8px 12px',
              borderRadius: 8,
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              fontSize: '.78rem',
              color: '#dc2626',
              fontWeight: 500,
            }}>
              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1, color: '#dc2626' }} />
              <span>{inepError}</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>Digite o CEP primeiro para auto-preencher.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 10 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">CEP *</label>
            <input className="form-input" name="cep" data-testid="input-escola-cep" value={form.cep} onChange={e => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 8);
              setForm(prev => ({ ...prev, cep: v }));
              if (v.length === 8) {
                fetch(`https://viacep.com.br/ws/${v}/json/`).then(r => r.json()).then(d => {
                  if (!d.erro) {
                    setForm(prev => {
                      const updated = {
                        ...prev,
                        logradouro: d.logradouro || prev.logradouro,
                        bairro: d.bairro || prev.bairro,
                        cidade: d.localidade || prev.cidade,
                        uf: d.uf || prev.uf
                      };
                      const query = [updated.logradouro, updated.bairro, updated.cidade, updated.uf, 'Brasil'].filter(Boolean).join(', ');
                      fetch(
                        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=br`,
                        { headers: { 'Accept-Language': 'pt-BR' } }
                      )
                        .then(res => res.json())
                        .then(geo => {
                          if (geo && geo.length > 0) {
                            setForm(f => ({
                              ...f,
                              latitude: String(parseFloat(geo[0].lat).toFixed(6)),
                              longitude: String(parseFloat(geo[0].lon).toFixed(6)),
                            }));
                          }
                        })
                        .catch(() => {});
                      return updated;
                    });
                  }
                }).catch(() => {});
              }
            }} placeholder="Ex: 00000000" maxLength={8} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Logradouro *</label><input className="form-input" name="logradouro" value={form.logradouro} onChange={e => setForm(prev => ({ ...prev, logradouro: e.target.value }))} /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Número *</label><input className="form-input" name="numero" value={form.numero} onChange={e => setForm(prev => ({ ...prev, numero: e.target.value }))} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 10 }}>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Bairro *</label><input className="form-input" name="bairro" value={form.bairro} onChange={e => setForm(prev => ({ ...prev, bairro: e.target.value }))} /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Cidade *</label><input className="form-input" name="cidade" value={form.cidade} onChange={e => setForm(prev => ({ ...prev, cidade: e.target.value }))} /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">UF *</label><input className="form-input" name="uf" value={form.uf} onChange={e => setForm(prev => ({ ...prev, uf: e.target.value.toUpperCase().slice(0,2) }))} maxLength={2} /></div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Complemento</label><input className="form-input" name="complemento" value={form.complemento} onChange={e => setForm(prev => ({ ...prev, complemento: e.target.value }))} /></div>

        {renderGeolocalizacaoSection()}
      </div>
    );
  };

  const renderGeolocalizacaoSection = () => (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--border-color, #e2e8f0)', paddingTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <label className="form-label" style={{ margin: 0, fontWeight: 700, color: 'var(--text-main, #0f172a)' }}>
          Geolocalização / Coordenadas *
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={handleGetDeviceLocation}
            className="btn-secondary"
            style={{
              fontSize: '.75rem',
              padding: '5px 12px',
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              cursor: 'pointer',
              borderRadius: 6,
              color: '#0f172a',
              fontWeight: 600,
            }}
          >
            📍 GPS Dispositivo
          </button>
          <button
            type="button"
            onClick={handleGetIpLocation}
            className="btn-secondary"
            style={{
              fontSize: '.75rem',
              padding: '5px 12px',
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              cursor: 'pointer',
              borderRadius: 6,
              color: '#0f172a',
              fontWeight: 600,
            }}
          >
            🌐 Obter por IP
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1.1fr', gap: 12, alignItems: 'end' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" style={{ fontSize: '.75rem', color: '#1e293b', fontWeight: 600 }}>
            Latitude *
          </label>
          <input
            className="form-input"
            value={form.latitude}
            onChange={(e) => setForm((prev) => ({ ...prev, latitude: e.target.value }))}
            placeholder="Ex: -19.916681"
          />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label" style={{ fontSize: '.75rem', color: '#1e293b', fontWeight: 600 }}>
            Longitude *
          </label>
          <input
            className="form-input"
            value={form.longitude}
            onChange={(e) => setForm((prev) => ({ ...prev, longitude: e.target.value }))}
            placeholder="Ex: -43.934493"
          />
        </div>
        <button
          type="button"
          onClick={() => handleGeocodeEscolaManual(false)}
          disabled={geocodingLoading}
          className="btn-primary"
          style={{
            height: '36px',
            padding: '0 10px',
            fontSize: '.78rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            cursor: geocodingLoading ? 'not-allowed' : 'pointer',
            background: 'linear-gradient(135deg,#3b82f6,#7c3aed)',
            border: 'none',
            color: '#fff',
            borderRadius: 8,
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(99,102,241,.25)',
          }}
        >
          {geocodingLoading ? '🔍 Localizando...' : '🔍 Buscar Endereço'}
        </button>
      </div>

      {/* Alerta se não houver coordenadas */}
      {(!form.latitude || !form.longitude) && (
        <div style={{ fontSize: '.78rem', color: '#f43f5e', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
          ⚠️ Sem coordenadas! É obrigatório informar a Latitude e a Longitude para posicionar a escola no mapa.
        </div>
      )}

      {/* Mini Mapa Interativo */}
      {form.latitude && form.longitude && isFinite(parseFloat(form.latitude)) && isFinite(parseFloat(form.longitude)) && (
        <div style={{ marginTop: 14, height: 170, borderRadius: 10, overflow: 'hidden', border: '1px solid #cbd5e1', position: 'relative' }}>
          <MapContainer
            center={[parseFloat(form.latitude), parseFloat(form.longitude)]}
            zoom={14}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[parseFloat(form.latitude), parseFloat(form.longitude)]} icon={escolaMarkerPinIcon} />
            <MapCenterController center={[parseFloat(form.latitude), parseFloat(form.longitude)]} />
            <MapClickEvents
              onClick={(lat, lng) =>
                setForm((f) => ({
                  ...f,
                  latitude: lat.toFixed(6),
                  longitude: lng.toFixed(6),
                }))
              }
            />
          </MapContainer>
          <div
            style={{
              position: 'absolute',
              bottom: 6,
              right: 6,
              zIndex: 1000,
              background: 'rgba(255,255,255,0.92)',
              backdropFilter: 'blur(4px)',
              padding: '3px 8px',
              borderRadius: 6,
              fontSize: '0.68rem',
              color: '#334155',
              fontWeight: 600,
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              pointerEvents: 'none',
            }}
          >
            Clique no mapa para ajustar o pino
          </div>
        </div>
      )}
    </div>
  );

  // Verificar se email do diretor ja existe
  const [diretorEmailCheck, setDiretorEmailCheck] = useState<string | null>(null);
  const checkDiretorEmail = (email: string) => {
    if (!email.trim()) { setDiretorEmailCheck(null); return; }
    const found = allUsuarios.find(u => u.email?.toLowerCase() === email.toLowerCase().trim());
    if (found) {
      setDiretorEmailCheck(`Ja existe um usuario com este e-mail: ${found.nome} (${found.perfil}).`);
    } else {
      setDiretorEmailCheck(null);
    }
  };

  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="form-group"><label className="form-label">Nome do Diretor *</label><input className="form-input" name="diretorNome" data-testid="input-diretor-nome" value={diretor.nome} onChange={e => setDiretor({ ...diretor, nome: e.target.value })} placeholder="Ex: Joao da Silva" /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="form-group">
          <label className="form-label">Email de Acesso *</label>
          <input className="form-input" name="diretorEmail" data-testid="input-diretor-email" type="email" value={diretor.email}
            onChange={e => { setDiretor({ ...diretor, email: e.target.value }); setDiretorEmailCheck(null); }}
            onBlur={e => checkDiretorEmail(e.target.value)}
            placeholder="Ex: diretor@escola.edu.br" />
          {diretorEmailCheck && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '8px 10px', borderRadius: 8, marginTop: 6, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', fontSize: '.78rem', color: '#dc2626', fontWeight: 500 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{diretorEmailCheck}</span>
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Telefone Diretor</label>
          <input className="form-input" name="diretorTelefone" data-testid="input-diretor-telefone" value={diretor.telefone}
            onChange={e => setDiretor({ ...diretor, telefone: e.target.value })}
            placeholder="Ex: (31) 99999-8888" />
        </div>
      </div>

      {/* Linha Suave de Separação de Passos */}
      <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 10, paddingTop: 14 }}>
        <label className="form-label" style={{ fontWeight: 800, color: 'var(--text-main)', fontSize: '0.84rem' }}>
          🚀 Opções de Onboarding e Ativação da Escola
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 8 }}>
          
          {/* Opção 1: 7 Telas Limpo */}
          <div
            onClick={() => {
              setOpcaoOnboarding('SETE_TELAS_LIMPO');
              setAcessoDireto(false);
            }}
            style={{
              padding: '12px 14px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s ease',
              border: opcaoOnboarding === 'SETE_TELAS_LIMPO' ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
              background: opcaoOnboarding === 'SETE_TELAS_LIMPO' ? '#f0f9ff' : '#fff',
              display: 'flex', flexDirection: 'column', gap: 8
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: opcaoOnboarding === 'SETE_TELAS_LIMPO' ? '#1d4ed8' : 'var(--text-main)' }}>
                1. Cadastrar 7 Telas (Limpo)
              </div>
              <span title="Navega sequencialmente pelos 7 cards de onboarding até o final. Entra no sistema limpo (sem dados demo)." style={{ display: 'inline-flex', cursor: 'help' }}>
                <HelpCircle size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
              </span>
            </div>

            <div
              onClick={(e) => {
                e.stopPropagation();
                localStorage.setItem('ac_onboarding_default_mode', 'SETE_TELAS_LIMPO');
                setModoPadraoSalvo('SETE_TELAS_LIMPO');
                setOpcaoOnboarding('SETE_TELAS_LIMPO');
                setAcessoDireto(false);
                toast.success('Modo "7 Telas (Limpo)" definido como padrão para novos cadastros!');
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.68rem', fontWeight: 600,
                color: modoPadraoSalvo === 'SETE_TELAS_LIMPO' ? '#2563eb' : 'var(--text-muted)',
                borderTop: '1px dashed #e2e8f0', paddingTop: 6, cursor: 'pointer'
              }}
            >
              <div
                style={{
                  width: 15, height: 15, borderRadius: 4,
                  border: modoPadraoSalvo === 'SETE_TELAS_LIMPO' ? '1.5px solid #2563eb' : '1.5px solid #cbd5e1',
                  background: modoPadraoSalvo === 'SETE_TELAS_LIMPO' ? '#2563eb' : '#ffffff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'all 0.15s ease'
                }}
              >
                {modoPadraoSalvo === 'SETE_TELAS_LIMPO' && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span>{modoPadraoSalvo === 'SETE_TELAS_LIMPO' ? '★ Padrão do Sistema' : 'Marcar como padrão'}</span>
            </div>
          </div>

          {/* Opção 2: 7 Telas com Dados de Demonstração */}
          <div
            onClick={() => {
              setOpcaoOnboarding('SETE_TELAS_DEMO');
              setAcessoDireto(false);
            }}
            style={{
              padding: '12px 14px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s ease',
              border: opcaoOnboarding === 'SETE_TELAS_DEMO' ? '1.5px solid #10b981' : '1px solid #e2e8f0',
              background: opcaoOnboarding === 'SETE_TELAS_DEMO' ? '#ecfdf5' : '#fff',
              display: 'flex', flexDirection: 'column', gap: 8
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: opcaoOnboarding === 'SETE_TELAS_DEMO' ? '#047857' : 'var(--text-main)' }}>
                2. 7 Telas com Dados Demo
              </div>
              <span title="Popula a escola com dados de demonstração (turmas/alunos) e libera a navegação pelas 7 telas do wizard." style={{ display: 'inline-flex', cursor: 'help' }}>
                <HelpCircle size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
              </span>
            </div>

            <div
              onClick={(e) => {
                e.stopPropagation();
                localStorage.setItem('ac_onboarding_default_mode', 'SETE_TELAS_DEMO');
                setModoPadraoSalvo('SETE_TELAS_DEMO');
                setOpcaoOnboarding('SETE_TELAS_DEMO');
                setAcessoDireto(false);
                toast.success('Modo "7 Telas com Dados Demo" definido como padrão para novos cadastros!');
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.68rem', fontWeight: 600,
                color: modoPadraoSalvo === 'SETE_TELAS_DEMO' ? '#059669' : 'var(--text-muted)',
                borderTop: '1px dashed #e2e8f0', paddingTop: 6, cursor: 'pointer'
              }}
            >
              <div
                style={{
                  width: 15, height: 15, borderRadius: 4,
                  border: modoPadraoSalvo === 'SETE_TELAS_DEMO' ? '1.5px solid #10b981' : '1.5px solid #cbd5e1',
                  background: modoPadraoSalvo === 'SETE_TELAS_DEMO' ? '#10b981' : '#ffffff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'all 0.15s ease'
                }}
              >
                {modoPadraoSalvo === 'SETE_TELAS_DEMO' && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span>{modoPadraoSalvo === 'SETE_TELAS_DEMO' ? '★ Padrão do Sistema' : 'Marcar como padrão'}</span>
            </div>
          </div>

          {/* Opção 3: Somente Equipe */}
          <div
            onClick={() => {
              setOpcaoOnboarding('SOMENTE_EQUIPE');
              setAcessoDireto(true);
            }}
            style={{
              padding: '12px 14px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s ease',
              border: opcaoOnboarding === 'SOMENTE_EQUIPE' ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
              background: opcaoOnboarding === 'SOMENTE_EQUIPE' ? '#eff6ff' : '#fff',
              display: 'flex', flexDirection: 'column', gap: 8
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: opcaoOnboarding === 'SOMENTE_EQUIPE' ? '#1d4ed8' : 'var(--text-main)' }}>
                3. Cadastrar Somente Equipe
              </div>
              <span title="Cadastra Diretor, Secretaria e Coordenador diretamente. Pula o wizard de onboarding e dá acesso imediato." style={{ display: 'inline-flex', cursor: 'help' }}>
                <HelpCircle size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
              </span>
            </div>

            <div
              onClick={(e) => {
                e.stopPropagation();
                localStorage.setItem('ac_onboarding_default_mode', 'SOMENTE_EQUIPE');
                setModoPadraoSalvo('SOMENTE_EQUIPE');
                setOpcaoOnboarding('SOMENTE_EQUIPE');
                setAcessoDireto(true);
                toast.success('Modo "Cadastrar Somente Equipe" definido como padrão para novos cadastros!');
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.68rem', fontWeight: 600,
                color: modoPadraoSalvo === 'SOMENTE_EQUIPE' ? '#2563eb' : 'var(--text-muted)',
                borderTop: '1px dashed #e2e8f0', paddingTop: 6, cursor: 'pointer'
              }}
            >
              <div
                style={{
                  width: 15, height: 15, borderRadius: 4,
                  border: modoPadraoSalvo === 'SOMENTE_EQUIPE' ? '1.5px solid #2563eb' : '1.5px solid #cbd5e1',
                  background: modoPadraoSalvo === 'SOMENTE_EQUIPE' ? '#2563eb' : '#ffffff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, transition: 'all 0.15s ease'
                }}
              >
                {modoPadraoSalvo === 'SOMENTE_EQUIPE' && (
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span>{modoPadraoSalvo === 'SOMENTE_EQUIPE' ? '★ Padrão do Sistema' : 'Marcar como padrão'}</span>
            </div>
          </div>

        </div>
      </div>

    </div>
  );

  const renderCredenciais = () => credenciais && (
    <InviteResultDialog
      open={true}
      nome={credenciais.nomeDiretor || credenciais.email}
      email={credenciais.email}
      perfil="ESCOLA"
      telefone={credenciais.telefone}
      activationLink={credenciais.activationLink}
      emailSent={credenciais.emailEnviado}
      onClose={() => { setCredenciais(null); setModalOpen(false); resetForm(); }}
    />
  );
  // ── Tabs para modo edição ─────────────────────────────────
  const CARGOS = [
    { key: 'DIRETOR', label: 'Direção' },
    { key: 'COORDENACAO', label: 'Coordenação' },
    { key: 'SECRETARIA', label: 'Secretaria' },
  ];

  const renderEditTabs = () => editing && (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, borderBottom: '2px solid var(--border, #e5e7eb)', marginBottom: 16 }}>
      {([
        { key: 'dados' as const, icon: <ClipboardList size={14} />, label: 'Dados' },
        { key: 'equipe' as const, icon: <Users size={14} />, label: 'Equipe' },
        { key: 'professores' as const, icon: <GraduationCap size={14} />, label: 'Professores' },
        { key: 'responsaveis' as const, icon: <HeartHandshake size={14} />, label: 'Responsáveis' },
        { key: 'alunos' as const, icon: <BookOpen size={14} />, label: 'Alunos' },
      ]).map(t => (
        <button key={t.key} onClick={() => setEditTab(t.key)} style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
          background: 'none', border: 'none', cursor: 'pointer',
          fontWeight: editTab === t.key ? 700 : 500, fontSize: '.84rem',
          color: editTab === t.key ? '#6366f1' : '#6b7280',
          borderBottom: editTab === t.key ? '2px solid #6366f1' : '2px solid transparent',
          marginBottom: -2, transition: 'all .15s', whiteSpace: 'nowrap',
        }}>{t.icon} {t.label}</button>
      ))}
    </div>
  );

  const renderEquipeCargoTabs = () => (
    <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
      {CARGOS.map(c => (
        <button key={c.key} onClick={() => setEquipeCargo(c.key)} style={{
          padding: '5px 14px', borderRadius: 6, fontSize: '.78rem', fontWeight: 600,
          background: equipeCargo === c.key ? '#6366f1' : 'var(--bg-muted, #f1f4f8)',
          color: equipeCargo === c.key ? 'white' : '#6b7280',
          border: 'none', cursor: 'pointer', transition: 'all .15s',
        }}>{c.label}</button>
      ))}
    </div>
  );

  const renderEditDadosForm = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Nome da Escola *</label>
            <input className="form-input" name="nome" value={form.nome} onChange={e => setForm(prev => ({ ...prev, nome: e.target.value }))} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Código INEP</label>
            <input
              className="form-input"
              style={inepError ? { borderColor: '#ef4444', background: 'rgba(239,68,68,.04)' } : {}}
              name="inep"
              value={form.codigoINEP}
              onChange={e => {
                const v = e.target.value;
                setForm(prev => ({ ...prev, codigoINEP: v }));
                if (inepError) setInepError(null);
              }}
              onBlur={() => {
                if (form.codigoINEP?.trim()) {
                  validarInep(form.codigoINEP, editing?.id);
                }
              }}
              placeholder="Ex: 31000000"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Esfera Administrativa *</label>
            <select name="tipoRede" className="form-select" value={form.tipoRede} onChange={e => setForm(prev => ({ ...prev, tipoRede: e.target.value }))}>
              {TIPO_REDE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        {inepError && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 8,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            fontSize: '.78rem',
            color: '#dc2626',
            fontWeight: 500,
          }}>
            <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1, color: '#dc2626' }} />
            <span>{inepError}</span>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Email da Escola *</label>
            <input className="form-input" type="email" name="email" value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} placeholder="Ex: escola@dominio.com" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Telefone</label>
            <input className="form-input" name="telefone" value={form.telefone} onChange={e => setForm(prev => ({ ...prev, telefone: e.target.value }))} placeholder="Ex: (31) 99999-9999" />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 10 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">CEP</label>
            <input className="form-input" name="cep" value={form.cep} onChange={e => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 8);
              setForm(prev => ({ ...prev, cep: v }));
              if (v.length === 8) {
                fetch(`https://viacep.com.br/ws/${v}/json/`).then(r => r.json()).then(d => {
                  if (!d.erro) {
                    setForm(prev => {
                      const updated = {
                        ...prev,
                        logradouro: d.logradouro || prev.logradouro,
                        bairro: d.bairro || prev.bairro,
                        cidade: d.localidade || prev.cidade,
                        uf: d.uf || prev.uf
                      };
                      const query = [updated.logradouro, updated.bairro, updated.cidade, updated.uf, 'Brasil'].filter(Boolean).join(', ');
                      fetch(
                        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=br`,
                        { headers: { 'Accept-Language': 'pt-BR' } }
                      )
                        .then(res => res.json())
                        .then(geo => {
                          if (geo && geo.length > 0) {
                            setForm(f => ({
                              ...f,
                              latitude: String(parseFloat(geo[0].lat).toFixed(6)),
                              longitude: String(parseFloat(geo[0].lon).toFixed(6)),
                            }));
                          }
                        })
                        .catch(() => {});
                      return updated;
                    });
                  }
                }).catch(() => {});
              }
            }} placeholder="Ex: 00000000" maxLength={8} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Logradouro *</label><input className="form-input" name="logradouro" value={form.logradouro} onChange={e => setForm(prev => ({ ...prev, logradouro: e.target.value }))} placeholder="Ex: Rua / Avenida..." /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Número</label><input className="form-input" name="numero" value={form.numero} onChange={e => setForm(prev => ({ ...prev, numero: e.target.value }))} placeholder="Ex: 100" /></div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 10 }}>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Bairro</label><input className="form-input" name="bairro" value={form.bairro} onChange={e => setForm(prev => ({ ...prev, bairro: e.target.value }))} placeholder="Ex: Centro" /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Cidade *</label><input className="form-input" name="cidade" value={form.cidade} onChange={e => setForm(prev => ({ ...prev, cidade: e.target.value }))} /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">UF *</label><input className="form-input" name="uf" value={form.uf} onChange={e => setForm(prev => ({ ...prev, uf: e.target.value.toUpperCase().slice(0,2) }))} maxLength={2} /></div>
        </div>

        <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">Complemento</label><input className="form-input" name="complemento" value={form.complemento} onChange={e => setForm(prev => ({ ...prev, complemento: e.target.value }))} placeholder="Ex: Sala 1, Bloco A" /></div>

        {renderGeolocalizacaoSection()}
      </div>
    );
  };

  const renderEditContent = () => {
    if (!editing) return null;
    if (editTab === 'dados') return renderEditDadosForm();
    if (editTab === 'equipe') return (
      <div>
        {renderEquipeCargoTabs()}
        <UserManagerTab
          key={equipeCargo}
          entityType="escola"
          entityId={editing.id}
          filterCargo={equipeCargo}
          label={CARGOS.find(c => c.key === equipeCargo)?.label || 'Equipe'}
          defaultCargo={equipeCargo}
        />
      </div>
    );
    if (editTab === 'professores') return (
      <UserManagerTab
        entityType="escola"
        entityId={editing.id}
        label="Professores"
        defaultPerfil="PROFESSOR"
        filterCargo="PROFESSOR"
      />
    );
    if (editTab === 'responsaveis') return <EscolaResponsaveisTab escolaId={editing.id} />;
    if (editTab === 'alunos') return <EscolaAlunosTab escolaId={editing.id} />;
    return null;
  };

  const renderFooter = () => {
    if (credenciais) return <button className="btn-save" onClick={() => setModalOpen(false)}>Fechar</button>;
    if (editing) {
      if (editTab !== 'dados') return <button className="btn-cancel" onClick={() => setModalOpen(false)}>Fechar</button>;
      return <><button className="btn-cancel" onClick={() => setModalOpen(false)}>Cancelar</button><button className="btn-save" onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button></>;
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <button className="btn-cancel" onClick={() => step > 1 ? setStep(step - 1) : handleCancelClick()}>
          {step > 1 ? '← Voltar' : 'Cancelar'}
        </button>
        {step < 4
          ? <button className="btn-save" disabled={inepValidating} onClick={async () => {
              if (step === 1) {
                if (!vinculoDefinido) { toast.error('Defina o vínculo hierárquico antes de continuar'); return; }
              }
              if (step === 2) {
                if (!form.nome.trim()) { toast.error('Informe o nome da escola'); return; }
                if (form.codigoINEP.trim()) {
                  const inepOk = await validarInep(form.codigoINEP);
                  if (!inepOk) return;
                }
              }
              if (step === 3) {
                if (!form.cep.trim() || !form.logradouro.trim() || !form.numero.trim() || !form.bairro.trim() || !form.cidade.trim() || !form.uf.trim()) {
                  toast.error('Preencha os campos obrigatórios de endereço'); return;
                }
                if (!form.latitude || !form.longitude || isNaN(parseFloat(form.latitude)) || isNaN(parseFloat(form.longitude))) {
                  toast.warning('Informe a Latitude e Longitude da escola (ou clique em Buscar Endereço).');
                  return;
                }
              }
              setStep(step + 1);
            }}>{inepValidating ? 'Verificando...' : 'Próximo →'}</button>
          : <button className="btn-save" onClick={handleSave} disabled={saving}>{saving ? 'Criando...' : 'Criar Escola'}</button>
        }
      </div>
    );
  };

  const totalAlunos = useMemo(() => {
    return scopedEscolas
      .filter((e: Escola) => e.ativo && !e.isDemo)
      .reduce((a: number, e: Escola) => a + (e._count?.alunos || 0), 0);
  }, [scopedEscolas]);
  const totalEscolas = useMemo(() => scopedEscolas.filter((e: Escola) => e.ativo && !e.isDemo).length, [scopedEscolas]);
  const escolasAtivas = totalEscolas;
  const tipoRedeCount = useMemo(() => {
    const c: Record<string, number> = {};
    scopedEscolas
      .filter((e: Escola) => e.ativo && !e.isDemo)
      .forEach((e: Escola) => { c[e.tipoRede] = (c[e.tipoRede] || 0) + 1; });
    return c;
  }, [scopedEscolas]);

  const renderEmptyStateForTabs = () => {
    if (searchQuery.trim()) {
      return (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}>
          <Search size={36} style={{ color: 'var(--text-muted, #9ca3af)', marginBottom: 12, opacity: 0.4, margin: '0 auto' }} />
          <div style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--text-main, #1e293b)' }}>Nenhuma escola encontrada</div>
          <div style={{ fontSize: '.78rem', marginTop: 4, color: 'var(--text-muted)' }}>Tente pesquisar por outros termos ou limpar os filtros.</div>
        </div>
      );
    }

    switch (filtroSetup) {
      case 'implantacao':
        return (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Clock size={44} style={{ color: '#3b82f6', marginBottom: 16, opacity: 0.7, margin: '0 auto 12px' }} />
            <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
              Não há nenhuma escola em fase de implantação
            </h3>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted, #6b7280)', maxWidth: 360, margin: '0 auto', lineHeight: 1.45 }}>
              Não temos nenhuma escola nesta fase. As escolas que iniciarem o assistente de configuração aparecerão aqui.
            </p>
          </div>
        );
      case 'atividade':
        return (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Building2 size={44} style={{ color: '#10b981', marginBottom: 16, opacity: 0.7, margin: '0 auto 12px' }} />
            <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
              Não há nenhuma escola ativa no momento
            </h3>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted, #6b7280)', maxWidth: 360, margin: '0 auto', lineHeight: 1.45 }}>
              Não há nenhuma escola com o setup concluído e em atividade no momento.
            </p>
          </div>
        );
      case 'sem-inicio':
        return (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <AlertTriangle size={44} style={{ color: '#f59e0b', marginBottom: 16, opacity: 0.7, margin: '0 auto 12px' }} />
            <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
              Nenhuma escola sem início
            </h3>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted, #6b7280)', maxWidth: 360, margin: '0 auto', lineHeight: 1.45 }}>
              Excelente! Todas as escolas cadastradas já iniciaram ou concluíram o processo de configuração.
            </p>
          </div>
        );
      case 'desativadas':
        return (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <AlertTriangle size={44} style={{ color: '#ef4444', marginBottom: 16, opacity: 0.7, margin: '0 auto 12px' }} />
            <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
              Nenhuma escola desativada
            </h3>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted, #6b7280)', maxWidth: 360, margin: '0 auto', lineHeight: 1.45 }}>
              Todas as escolas vinculadas estão ativas e em pleno funcionamento.
            </p>
          </div>
        );
      default:
        return (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.85rem' }}>
            Nenhuma escola encontrada para a aba selecionada.
          </div>
        );
    }
  };

  return (
    <div className="admin-page animate-fadeIn escolas-page-compact">

      {/* ═══ HERO BANNER ═══ */}
      <div className="escolas-hero-banner" style={{
        background: 'linear-gradient(135deg, #065f46 0%, #047857 40%, #059669 100%)',
        borderRadius: 18, padding: 'clamp(12px, 1.4vw, 18px)', marginBottom: 12, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(16,185,129,.12)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="escolas-hero-inner">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                <Building2 size={16} style={{ color: '#6ee7b7' }} />
                <span style={{ color: 'rgba(255,255,255,.45)', fontSize: '.66rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>Gestao</span>
              </div>
              <h1 style={{ color: 'white', fontSize: 'clamp(1.05rem, 1.6vw, 1.28rem)', fontWeight: 800, margin: 0 }}>Escolas</h1>
              <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.75rem', marginTop: 2 }}>
                {totalEscolas} escolas {isEstadual ? 'no estado' : 'cadastradas'} · {escolasAtivas} ativas · {totalAlunos.toLocaleString('pt-BR')} alunos
              </p>
            </div>
            <div className="escolas-hero-actions">
              {!isEstadual && (
                <button 
                  type="button"
                  onClick={() => {
                    toast.warning('A criação de escolas demo está temporariamente desativada.');
                  }} 
                  className="escolas-btn-demo"
                  title="Criação de escolas demo temporariamente desativada"
                >
                  <Globe size={14} style={{ color: '#94a3b8' }} /> Escola Demo
                  <span style={{ fontSize: '0.62rem', background: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5', padding: '1px 5px', borderRadius: 4, marginLeft: 2 }}>Pausado</span>
                </button>
              )}
              {canCreateEscola && (
                <button onClick={openNew} className="escolas-btn-nova">
                  <Plus size={14} /> Nova Escola
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .escolas-page-compact {
          margin-top: -22px;
          height: auto;
          display: flex;
          flex-direction: column;
          overflow: visible;
        }
        .escolas-mobile-toolbar {
          display: none;
        }
        .escolas-desktop-controls {
          display: flex;
          flex-direction: column;
        }
        .escolas-hero-inner {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
        }
        .escolas-hero-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-shrink: 0;
        }
        .escolas-btn-demo {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 8px 14px;
          border-radius: 9px;
          background: rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.7);
          font-weight: 700;
          font-size: .8rem;
          border: 1px solid rgba(255,255,255,0.15);
          cursor: pointer;
          white-space: nowrap;
        }
        .escolas-btn-nova {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 8px 14px;
          border-radius: 9px;
          background: #6ee7b7;
          color: #065f46;
          font-weight: 700;
          font-size: .8rem;
          border: none;
          cursor: pointer;
          white-space: nowrap;
        }
        .escolas-tier2-controls {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          border-bottom: 1px solid rgba(255,255,255,.15);
          background: #111827;
        }
        .escolas-search-group {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
        }
        .escolas-search-input-wrapper {
          position: relative;
        }
        .escolas-search-input {
          height: 34px;
          width: 280px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,.15);
          padding: 0 10px 0 30px;
          font-size: .82rem;
          background: rgba(255,255,255,.08);
          color: rgba(255,255,255,.9);
        }
        .escolas-select-cidade {
          height: 34px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,.15);
          padding: 0 10px;
          font-size: .82rem;
          background: #111827;
          color: rgba(255,255,255,.9);
        }
        .escolas-tier2-right {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
        }
        .escolas-pagesize-select {
          height: 32px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,.15);
          padding: 0 8px;
          font-size: .78rem;
          background: #111827;
          color: rgba(255,255,255,.9);
        }
        .escolas-page-compact .escolas-list-scroll {
          flex: none !important;
          height: auto !important;
          overflow-y: visible !important;
        }
        .escolas-list-grid {
          display: grid;
          grid-template-columns: minmax(0, 2fr) minmax(110px, .9fr) minmax(120px, 1fr) minmax(80px, .6fr) minmax(80px, .6fr) minmax(110px, .9fr) 92px;
          gap: 12px;
          align-items: center;
        }
        .escolas-list-row {
          padding: 6px 10px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }
        .escolas-list-row:last-child { border-bottom: none; }
        .escolas-cards-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
        }
        .escolas-list-scroll::-webkit-scrollbar { width: 10px; }
        .escolas-list-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 999px; border: 2px solid transparent; background-clip: padding-box; }
        .escolas-list-item {
          background: var(--bg-main, #f8fafc);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 8px;
          margin: 3px 8px;
        }
        @media (max-width: 1024px) {
          .escolas-list-grid { grid-template-columns: minmax(0, 2fr) minmax(110px, .9fr) minmax(95px, .8fr) 88px; }
          .escolas-hide-md { display: none !important; }
          .escolas-cards-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 768px) {
          .escolas-page-compact {
            margin-top: -8px;
            height: auto;
            overflow: visible;
          }
          .escolas-hero-actions {
            display: none !important;
          }
          .escolas-desktop-controls {
            display: none !important;
          }
          .escolas-mobile-toolbar {
            display: block !important;
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
            overflow: hidden;
          }
          .escolas-list-grid {
            grid-template-columns: minmax(0, 1fr) auto !important;
            gap: 8px;
          }
          .escolas-hide-sm { display: none !important; }
          .escolas-cards-grid { grid-template-columns: 1fr; }
        }
      `}</style>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Carregando...</div>
      ) : (data || []).length === 0 ? (
        <div style={{
          background: 'var(--bg-card, white)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 14,
          padding: '60px 20px',
          textAlign: 'center',
          marginTop: 12
        }}>
          <Building2 size={48} style={{ color: 'var(--text-muted, #9ca3af)', marginBottom: 16, opacity: 0.5, margin: '0 auto 12px' }} />
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-main, #1e293b)', margin: '0 0 8px' }}>
            Nenhuma escola cadastrada ainda
          </h3>
          <p style={{ fontSize: '.85rem', color: 'var(--text-muted, #6b7280)', maxWidth: 380, margin: '0 auto 20px', lineHeight: 1.5 }}>
            Cadastre a primeira escola para começar a gerenciar e acompanhar o progresso de alunos, professores e turmas.
          </p>
        <button type="button" className="btn-primary" onClick={openNew} style={{ height: 38, padding: '0 20px', fontSize: '.85rem', display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 10, margin: '0 auto' }}>
            <Plus size={16} /> Cadastrar Escola
          </button>
        </div>
      ) : (
      <div style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 14, overflow: 'visible', marginBottom: 32, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        
        {/* MOBILE TOOLBAR OFICIAL (Lupa à esquerda, Botões de Ação no centro, Gaveta de Ajustes/Filtros à direita) */}
        <div className="escolas-mobile-toolbar">
          <DataToolbar
            busca={searchQuery}
            onBuscaChange={setSearchQuery}
            placeholder="Buscar por nome, código..."
            actions={
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {!isEstadual && (
                  <button
                    type="button"
                    onClick={() => toast.warning('A criação de escolas demo está temporariamente desativada.')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '0 10px',
                      height: 32,
                      borderRadius: 8,
                      background: 'var(--bg-main, #f1f5f9)',
                      color: 'var(--text-muted, #64748b)',
                      fontWeight: 700,
                      fontSize: '.75rem',
                      border: '1px solid var(--border-color, #e2e8f0)',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    <Globe size={13} style={{ color: '#94a3b8' }} /> Demo
                    <span style={{ fontSize: '0.6rem', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', padding: '1px 4px', borderRadius: 4 }}>Pausado</span>
                  </button>
                )}
                {canCreateEscola && (
                  <button
                    type="button"
                    onClick={openNew}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '0 12px',
                      height: 32,
                      borderRadius: 8,
                      background: '#10b981',
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '.75rem',
                      border: 'none',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      boxShadow: '0 1px 2px rgba(16,185,129,.2)',
                    }}
                  >
                    <Plus size={14} /> Nova Escola
                  </button>
                )}
              </div>
            }
            filters={
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', padding: '4px 0' }}>
                <div>
                  <label style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Status da Escola</label>
                  <select
                    value={filtroSetup}
                    onChange={(e) => setFiltroSetup(e.target.value as any)}
                    style={{
                      width: '100%',
                      height: 36,
                      borderRadius: 8,
                      border: '1px solid var(--border-color, #e5e7eb)',
                      padding: '0 10px',
                      fontSize: '.82rem',
                      background: 'var(--bg-card, white)',
                      color: 'var(--text-main, #1e293b)',
                    }}
                  >
                    {[
                      { key: 'todos', label: 'Todas as escolas', count: setupCounts.todos },
                      { key: 'implantacao', label: 'Em implantação', count: setupCounts.implantacao },
                      { key: 'atividade', label: 'Em atividade', count: setupCounts.atividade },
                      { key: 'sem-inicio', label: 'Sem início', count: setupCounts['sem-inicio'] },
                      { key: 'desativadas', label: 'Desativadas', count: setupCounts.desativadas },
                    ].map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label} ({t.count})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' }}>Cidade</label>
                  <select
                    value={filtroCidade}
                    onChange={(e) => setFiltroCidade(e.target.value)}
                    style={{
                      width: '100%',
                      height: 36,
                      borderRadius: 8,
                      border: '1px solid var(--border-color, #e5e7eb)',
                      padding: '0 10px',
                      fontSize: '.82rem',
                      background: 'var(--bg-card, white)',
                      color: 'var(--text-main, #1e293b)',
                    }}
                  >
                    <option value="">Todas as cidades</option>
                    {cidadesDisponiveis.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                {!isEstadual && tabDemoEscolas > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem', fontWeight: 500, color: 'var(--text-main)', cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={exibirDemo}
                        onChange={(e) => setExibirDemo(e.target.checked)}
                        style={{ accentColor: '#6366f1', width: 15, height: 15, cursor: 'pointer' }}
                      />
                      <span>Exibir {tabDemoEscolas} demo{tabDemoEscolas !== 1 ? 's' : ''}</span>
                    </label>
                  </div>
                )}
              </div>
            }
            totalRecords={escolasFiltradas.length}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[6, 12, 24, 48]}
            viewMode={viewMode}
            onViewModeChange={(m) => setViewMode(m as 'lista' | 'cards')}
            viewModes={['lista', 'cards']}
          />
        </div>

        {/* CONTROLES DESKTOP INTACTOS (Abas integradas + Tier 2) */}
        <div className="escolas-desktop-controls">
          {/* Abas integradas no tema escuro (Desktop) */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: '#111827',
            borderBottom: '1px solid rgba(255,255,255,.1)',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12
          }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { key: 'todos', label: 'Todas', count: setupCounts.todos },
                { key: 'implantacao', label: 'Em implantação', count: setupCounts.implantacao },
                { key: 'atividade', label: 'Em atividade', count: setupCounts.atividade },
                { key: 'sem-inicio', label: 'Sem início', count: setupCounts['sem-inicio'] },
                { key: 'desativadas', label: 'Desativadas', count: setupCounts.desativadas, isDanger: true },
              ].map((tab) => {
                const active = filtroSetup === tab.key;
                const isDanger = (tab as any).isDanger;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFiltroSetup(tab.key as typeof filtroSetup)}
                    style={{
                      height: 32,
                      padding: '0 12px',
                      borderRadius: 8,
                      border: active
                        ? (isDanger ? '1px solid #f87171' : '1px solid #818cf8')
                        : '1px solid rgba(255,255,255,.15)',
                      background: active
                        ? (isDanger ? 'rgba(239,68,68,.22)' : 'rgba(129,140,248,.18)')
                        : 'rgba(255,255,255,.04)',
                      color: active
                        ? (isDanger ? '#fca5a5' : '#a5b4fc')
                        : (isDanger && tab.count > 0 ? '#f87171' : 'rgba(255,255,255,.6)'),
                      fontSize: '.78rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all .15s ease',
                    }}
                  >
                    {tab.label} ({tab.count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tier 2: Busca e totalizador */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            borderBottom: '1px solid rgba(255,255,255,.15)',
            background: '#111827',
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,.4)' }} />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por nome, código, tipo..."
                  style={{ height: 34, width: 280, borderRadius: 8, border: '1px solid rgba(255,255,255,.15)', padding: '0 10px 0 30px', fontSize: '.82rem', background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.9)' }}
                />
              </div>
              <select
                value={filtroCidade}
                onChange={(e) => setFiltroCidade(e.target.value)}
                style={{ height: 34, borderRadius: 8, border: '1px solid rgba(255,255,255,.15)', padding: '0 10px', fontSize: '.82rem', background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.9)' }}
              >
                <option value="">Todas as cidades</option>
                {cidadesDisponiveis.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {/* Checkbox sutil Exibir Demos (apenas se houver escolas demo na aba) */}
              {!isEstadual && tabDemoEscolas > 0 && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: '.78rem',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,.75)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'color .15s ease',
                }}>
                  <input
                    type="checkbox"
                    checked={exibirDemo}
                    onChange={e => setExibirDemo(e.target.checked)}
                    style={{ accentColor: '#6366f1', width: 14, height: 14, cursor: 'pointer' }}
                  />
                  <span>Exibir {tabDemoEscolas} demo{tabDemoEscolas !== 1 ? 's' : ''}</span>
                </label>
              )}
              <span style={{ fontSize: '.78rem', color: 'rgba(255,255,255,.6)' }}>{escolasFiltradas.length} registro(s)</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                title="Itens por página"
                style={{ height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,.15)', padding: '0 8px', fontSize: '.78rem', background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.9)' }}
              >
                {[6, 12, 24, 48].map((n) => <option key={n} value={n}>{n}/página</option>)}
              </select>
              <div style={{ display: 'flex', border: '1px solid rgba(255,255,255,.15)', borderRadius: 8, overflow: 'hidden' }}>
                <button onClick={() => setViewMode('lista')} title="Visualização em lista" style={{ height: 32, width: 36, border: 'none', background: viewMode === 'lista' ? 'rgba(255,255,255,.15)' : 'rgba(255,255,255,.04)', color: viewMode === 'lista' ? '#a5b4fc' : 'rgba(255,255,255,.4)', cursor: 'pointer' }}><List size={14} style={{ margin: '0 auto' }} /></button>
                <button onClick={() => setViewMode('cards')} title="Visualização em cards" style={{ height: 32, width: 36, border: 'none', borderLeft: '1px solid rgba(255,255,255,.15)', background: viewMode === 'cards' ? 'rgba(255,255,255,.15)' : 'rgba(255,255,255,.04)', color: viewMode === 'cards' ? '#a5b4fc' : 'rgba(255,255,255,.4)', cursor: 'pointer' }}><LayoutGrid size={14} style={{ margin: '0 auto' }} /></button>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div className="escolas-list-scroll" style={{ flex: 1, overflowY: 'auto' }}>
            {escolasFiltradas.length === 0 ? (
              renderEmptyStateForTabs()
            ) : viewMode === 'lista' ? (
              <div>
                <div className="escolas-list-grid escolas-list-row" style={{ position: 'sticky', top: 0, zIndex: 2, fontSize: '.64rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', background: 'var(--bg-main, #f8fafc)' }}>
                  <div>Escola</div>
                  <div className="escolas-hide-sm">Esfera Adm.</div>
                  <div className="escolas-hide-sm">Rede Mantenedora</div>
                  <div className="escolas-hide-sm">Alunos</div>
                  <div className="escolas-hide-md">Equipe</div>
                  <div className="escolas-hide-md">Setup</div>
                  <div style={{ textAlign: 'right' }}>Ações</div>
                </div>
                {paginatedEscolas.map((escola) => {
                  const cidade = escola.municipio?.nome || escola.cidade || '—';
                  const estado = escola.municipio?.estado?.sigla || escola.uf || '';
                  const isDemo = escola.isDemo || (escola.nome || '').toLowerCase().includes('demo');
                  const redeNome = (escola.municipio?.estado as any)?.rede?.nome || (escola as any).rede?.nome || '—';
                  return (
                    <div key={escola.id} className="escolas-list-item escolas-list-grid escolas-list-row">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '.92rem', fontWeight: 650, color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{escola.nome}</span>
                          {isDemo && (
                            <span style={{
                              padding: '1px 5px',
                              borderRadius: 4,
                              fontSize: '.62rem',
                              fontWeight: 700,
                              background: 'rgba(59, 130, 246, 0.15)',
                              color: '#2563eb',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              textTransform: 'uppercase',
                              letterSpacing: '.04em',
                            }}>
                              Demo
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '.66rem', color: 'var(--text-muted)', marginTop: 0 }}>{cidade}{estado ? ` / ${estado}` : ''}</div>
                      </div>
                      <div className="escolas-hide-sm">{renderTipoBadge(escola.tipoRede)}</div>
                      <div className="escolas-hide-sm">
                        <span className="badge" style={{ background: '#818cf818', color: '#6366f1', border: '1px solid #818cf833', fontWeight: 600, fontSize: '.7rem' }}>
                          {redeNome}
                        </span>
                      </div>
                      <div className="escolas-hide-sm"><span className="badge badge-active">{escola._count?.alunos || 0}</span></div>
                      <div className="escolas-hide-md">{renderEquipeBadge(escola)}</div>
                      <div className="escolas-hide-md">{renderSetupBadge(escola)}</div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{renderActions(escola)}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="escolas-cards-grid" style={{ padding: 12 }}>
                {paginatedEscolas.map((escola) => {
                  const cidade = escola.municipio?.nome || escola.cidade || '—';
                  const estado = escola.municipio?.estado?.sigla || escola.uf || '';
                  const isDemo = escola.isDemo || (escola.nome || '').toLowerCase().includes('demo');
                  return (
                    <div
                      key={escola.id}
                      style={{
                        border: '1px solid var(--border-color, #e2e8f0)',
                        borderRadius: 14,
                        padding: '12px 14px',
                        background: 'var(--bg-card, #ffffff)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {/* Card Header: Ícone + Título/Cidade + Badge Tipo + Menu de Ações (3 pontos) */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, minWidth: 0, flex: 1 }}>
                          <div style={{
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            background: isDemo ? 'rgba(59, 130, 246, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                            color: isDemo ? '#2563eb' : '#059669',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginTop: 1,
                          }}>
                            <Building2 size={18} />
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--text-main, #0f172a)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {escola.nome}
                              </span>
                              {isDemo && (
                                <span style={{
                                  padding: '1px 5px',
                                  borderRadius: 4,
                                  fontSize: '.6rem',
                                  fontWeight: 800,
                                  background: 'rgba(59, 130, 246, 0.15)',
                                  color: '#2563eb',
                                  border: '1px solid rgba(59, 130, 246, 0.3)',
                                  textTransform: 'uppercase',
                                  letterSpacing: '.04em',
                                }}>
                                  Demo
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '.72rem', color: 'var(--text-muted, #64748b)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                              <MapPin size={11} style={{ opacity: 0.7 }} />
                              <span>{cidade}{estado ? ` / ${estado}` : ''}</span>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          {renderTipoBadge(escola.tipoRede)}
                          {renderActions(escola)}
                        </div>
                      </div>

                      {/* Card Metrics Grid: 4 chips compactos e explicativos em 2 colunas */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 6,
                        background: 'var(--bg-main, #f8fafc)',
                        padding: '8px 10px',
                        borderRadius: 10,
                        border: '1px solid var(--border-color, #f1f5f9)',
                      }}>
                        {/* Chip 1: Alunos */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <GraduationCap size={13} style={{ color: '#6366f1', flexShrink: 0 }} />
                          <span style={{ fontSize: '.73rem', fontWeight: 650, color: 'var(--text-main, #1e293b)' }}>
                            {escola._count?.alunos || 0} alunos
                          </span>
                        </div>

                        {/* Chip 2: Equipe */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Users size={13} style={{ color: '#0ea5e9', flexShrink: 0 }} />
                          <span style={{ fontSize: '.73rem', fontWeight: 650, color: 'var(--text-main, #1e293b)' }}>
                            {escola._count?.professorEscolas || 0} equipe
                          </span>
                        </div>

                        {/* Chip 3: Status Ativação */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            background: escola.ativo ? '#10b981' : '#ef4444',
                            flexShrink: 0,
                          }} />
                          <span style={{ fontSize: '.73rem', fontWeight: 650, color: escola.ativo ? '#059669' : '#dc2626' }}>
                            {escola.ativo ? 'Ativa' : 'Inativa'}
                          </span>
                        </div>

                        {/* Chip 4: Setup */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <ClipboardList size={13} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                          <div style={{ minWidth: 0, overflow: 'hidden' }}>
                            {renderSetupBadge(escola)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderTop: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-main, #f8fafc)' }}>
            <span style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>
              Página {currentPageSafe} de {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPageSafe <= 1}
                style={{ height: 30, width: 30, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card)', color: 'var(--text-main)', cursor: currentPageSafe <= 1 ? 'not-allowed' : 'pointer', opacity: currentPageSafe <= 1 ? 0.45 : 1 }}
              ><ChevronLeft size={14} style={{ margin: '0 auto' }} /></button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPageSafe >= totalPages}
                style={{ height: 30, width: 30, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card)', color: 'var(--text-main)', cursor: currentPageSafe >= totalPages ? 'not-allowed' : 'pointer', opacity: currentPageSafe >= totalPages ? 0.45 : 1 }}
              ><ChevronRight size={14} style={{ margin: '0 auto' }} /></button>
            </div>
          </div>
        </div>
      </div>
      )}
      <Modal open={modalOpen} onClose={() => {
          if (!saving) {
            if (!editing && !credenciais && hasFormData()) {
              setDiscardConfirmOpen(true);
            } else {
              setModalOpen(false);
            }
          }
        }}
        preventClose={!editing && !credenciais && hasFormData()}
        onAttemptClose={handleCancelClick}
        title={credenciais ? 'Escola Criada' : editing ? `Editar: ${editing.nome}` : 'Nova Escola'}
        footer={renderFooter()}>
        <style>{`.modal-content { max-width: 680px !important; }`}</style>
        {editing ? (
          <>{renderEditTabs()}{renderEditContent()}</>
        ) : (
          <>{renderStepper()}{credenciais ? renderCredenciais() : step <= 3 ? renderStep2() : renderStep3()}</>
        )}
      </Modal>

      {/* Diálogo Institucional de Confirmação para Descarte de Dados */}
      <ConfirmModal
        open={discardConfirmOpen}
        title="Descartar alterações?"
        message="Tem certeza que deseja cancelar o cadastro da escola? Todas as informações preenchidas serão perdidas."
        confirmLabel="Sim, descartar"
        cancelLabel="Continuar preenchendo"
        variant="warning"
        onCancel={() => setDiscardConfirmOpen(false)}
        onConfirm={handleConfirmDiscard}
      />

      <ConfirmModal
        open={!!deleteConfirm}
        title="Desativar escola"
        message={deleteConfirm ? `Desativar a escola "${deleteConfirm.nome}"? Os dados permanecem no sistema; apenas o cadastro fica inativo.` : ''}
        confirmLabel="Desativar"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDeleteEscola()}
      />

      <DemoInstallWizard
        isOpen={demoModalOpen}
        onClose={() => setDemoModalOpen(false)}
        onRefreshList={refetch}
        escolasExistentes={data || []}
      />

      {credenciaisEscola && (
        <EscolaAcessosRapidosModal
          escolaId={credenciaisEscola.id}
          escolaNome={credenciaisEscola.nome}
          isOpen={true}
          onClose={() => setCredenciaisEscola(null)}
        />
      )}

      {/* Modal de Confirmação com Senha de Segurança para Remoção Permanente de Escola */}
      <PasswordConfirmModal
        open={!!escolaToHardDelete}
        title="Remover Escola Permanentemente"
        description={`Para remover permanentemente a escola "${escolaToHardDelete?.nome}" e todos os seus registros, digite a palavra-chave de autorização:`}
        requiredPassword="marcelo"
        confirmLabel="Remover Escola"
        onCancel={() => setEscolaToHardDelete(null)}
        onConfirm={confirmHardDeleteEscola}
      />
    </div>
  );
}
